import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGame } from '../../contexts/GameContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useWallet } from '../../contexts/WalletContext';
import SoundUtils from '../../utils/soundUtils';
import CardUtils from '../../utils/CardUtils';
import CurrencyService from '../../services/CurrencyService';
import { fullscreenService } from '../../utils/fullscreenService';
import { autoArrangeCards, createGroupFromSelectedCards, cleanupSingleCardGroups } from '../EnhancedRummyGame/utils/cardGrouping';
import GameTable from '../EnhancedRummyGame/components/GameTable';
import '../EnhancedRummyGame/EnhancedRummyGame.css';
import PointTableModal from '../PointTableModal';
import GameSideBar from '../EnhancedRummyGame/components/GameSideBar';
import { classifyGroup } from '../../utils/groupRules';
import PinchZoom from '../EnhancedRummyGame/components/PinchZoom';
import DealHistoryModal from '../DealHistoryModal';
import RoundPointsPanel from '../RoundPointsPanel';
import CardDeckPicker from '../EnhancedRummyGame/components/CardDeckPicker';
import useGameVoice from '../../hooks/useGameVoice';

const useAnimationFrame = (callback) => {
  const requestRef = useRef();
  const previousTimeRef = useRef();
  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = time - previousTimeRef.current;
      callback(deltaTime);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [callback]);
};

const removeCardFromHand = (hand, cardId) => {
  const newHand = [];
  for (const item of hand) {
    if (item.isGroup) {
      const newGroupCards = item.cards.filter(c => c.id !== cardId);
      if (newGroupCards.length > 0) {
        newHand.push({ ...item, cards: newGroupCards, cardCount: newGroupCards.length });
      }
    } else {
      if (item.id !== cardId) {
        newHand.push(item);
      }
    }
  }
  return newHand;
};

const EnhancedRummyTable = ({
  user,
  onGameEnd,
  gameId = null,
  isSingleWindow = false,
  screenDimensions = null,
  isMobile = false,
  isLandscape = false,
  onLeaveGame,
  cashGameInfo = null,
  roomConfig = null
}) => {
  const params = useParams();
  const actualGameId = gameId || params.gameId;
  const isPractice = String(actualGameId || '').startsWith('practice_');

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const gameContext = useGame();
  const {
    gameState: multiplayerGameState,
    gameStatus: multiplayerGameStatus,
    isMyTurn: contextIsMyTurn,
    error: multiplayerError,
    drawCard: contextDrawCard,
    discardCard: contextDiscardCard,
    declareRummy: contextDeclareRummy,
    startNextDeal: contextStartNextDeal,
    dropFromGame: contextDropFromGame,
    socket,
    setCurrentGameId,
    resetGameState,
    updatePreservedCards,
    isInVoiceChat,
    isMuted,
    voiceParticipants,
    hasVoiceChat,
    joinVoiceChat,
    leaveVoiceChat,
    toggleVoiceMute,
    sendVoiceSignal,
    sendVoiceActivity
  } = gameContext;

  const { refreshWallet } = useWallet();

  const error = multiplayerError;

  const [localGameState, setLocalGameState] = useState(null);
  const gameState = localGameState || multiplayerGameState;

  // 🎙️ Voice chat is now just mic + speaker icons on the left side bar (no modal).
  const voiceRoomCode = gameState?.roomCode || null;
  const voiceEnabled = !isPractice && !!voiceRoomCode && (
    !!hasVoiceChat ||
    (!cashGameInfo && !String(voiceRoomCode).startsWith('FP') && !String(voiceRoomCode).startsWith('CR'))
  );
  const voice = useGameVoice({
    socket,
    currentUserId: currentUser?.id,
    players: (gameState?.players || []).map(p => ({ userId: p.userId, username: p.username })),
    roomCode: voiceRoomCode,
    gameId: actualGameId,
    enabled: voiceEnabled
  });
  const handleToggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    SoundUtils.setMuted(!next);
    try { localStorage.setItem('gameMuted', String(!next)); } catch (e) { /* ignore */ }
  };

  const [selectedCardsForGroup, setSelectedCardsForGroup] = useState([]);
  const [showGroupButton, setShowGroupButton] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isArrangeUsed, setIsArrangeUsed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [seatingPhase, setSeatingPhase] = useState(false);
  const [seatingData, setSeatingData] = useState(null);
  const [seatingCountdown, setSeatingCountdown] = useState(0);
  const seatingTimerRef = useRef(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [socketReady, setSocketReady] = useState(false);
  const [showPointTable, setShowPointTable] = useState(false);
  const [showDealHistory, setShowDealHistory] = useState(false);
  const [soundOn, setSoundOn] = useState(() => !SoundUtils.isMuted());
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [pointTableData, setPointTableData] = useState([]);
  const [pointTableTitle, setPointTableTitle] = useState('Point Table');
  const [pointTableMessage, setPointTableMessage] = useState('');
  const [pointTableCountdown, setPointTableCountdown] = useState(null);
  const [isGameOver, setIsGameOver] = useState(false);

  const [hasDrawnThisTurn, setHasDrawnThisTurn] = useState(false);
  const hasDrawnThisTurnRef = useRef(false);

  const [isSubmissionPhase, setIsSubmissionPhase] = useState(false);
  const [submissionTimeLeft, setSubmissionTimeLeft] = useState(null);
  const [declarationNotification, setDeclarationNotification] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasSubmittedRef = useRef(false);
  const isSubmissionPhaseRef = useRef(false);
  const [showDeclareConfirm, setShowDeclareConfirm] = useState(false);
  const autoSubmitTimeoutRef = useRef(null);
  const submissionTimerIntervalRef = useRef(null);
  
  // ===== WRONG DECLARATION POPUP STATE =====
  const [wrongDeclarationInfo, setWrongDeclarationInfo] = useState(null);
  // { message: string, playerName: string, visible: boolean }

  // ===== PERSISTENT TABLE: WAITING ROOM & AUTO-DROP STATE =====
  const [waitingRoomData, setWaitingRoomData] = useState(() => {
    if (cashGameInfo?.roomCode || roomConfig?.roomCode) {
      const cfg = cashGameInfo?.roomCode ? cashGameInfo : roomConfig;
      return {
        roomCode: cfg.roomCode || null,
        playerCount: cfg.playerCount || 1,
        maxPlayers: cfg.maxPlayers || 2,
        players: [],
        timerRunning: false,
        status: 'waiting'
      };
    }
    return null;
  });
  const [roomCountdown, setRoomCountdown] = useState(null);
  // true once the table has moved past the waiting stage (seating / playing) - hides the waiting HUD for good
  const [leftWaiting, setLeftWaiting] = useState(false);
  const waitingPlayersRef = useRef([]);
  useEffect(() => { waitingPlayersRef.current = waitingRoomData?.players || []; }, [waitingRoomData]);
  useEffect(() => { setLeftWaiting(false); }, [actualGameId]);
  const [isAutoDropQueued, setIsAutoDropQueued] = useState(false);
  const [showDropConfirm, setShowDropConfirm] = useState(false);
  const [showAutoDropConfirm, setShowAutoDropConfirm] = useState(false);
  const [dropNotice, setDropNotice] = useState(null); // { cumulative, penalty, maxPoints } when a drop is not allowed
  const [showRounds, setShowRounds] = useState(false); // round-by-round points panel (left pane)
  const [showDecks, setShowDecks] = useState(false);    // card-style (deck) picker
  const [cardDeck, setCardDeck] = useState(() => CardUtils.getDeck());
  const chooseDeck = (id) => { CardUtils.setDeck(id); setCardDeck(id); };

  // Points rummy: a drop that would take the player to / past the limit eliminates them -> not allowed.
  const getDropBlock = () => {
    if (gameState?.gameMode !== 'points' || !gameState?.maxPoints) return null;
    const me = gameState?.players?.find(p => String(p.userId) === String(currentUser?.id));
    if (!me) return null;
    const cumulative = Number(me.cumulativeScore) || 0;
    const penalty = me.hasDrawnAnyCard ? 40 : (Number(gameState.maxPoints) === 101 ? 20 : 25);
    if (cumulative + penalty >= gameState.maxPoints) return { cumulative, penalty, maxPoints: gameState.maxPoints };
    return null;
  };

  // Auto-drop handlers
  const handleToggleAutoDrop = () => {
    if (isAutoDropQueued) {
      setIsAutoDropQueued(false);
      socket?.emit('queue-auto-drop', { gameId: actualGameId, enable: false });
    } else {
      const block = getDropBlock();
      if (block) { setDropNotice(block); return; }
      setShowAutoDropConfirm(true);
    }
  };

  const handleConfirmAutoDrop = () => {
    setShowAutoDropConfirm(false);
    setIsAutoDropQueued(true);
    socket?.emit('queue-auto-drop', { gameId: actualGameId, enable: true });
  };

  const handleConfirmDrop = async () => {
    setShowDropConfirm(false);
    await handleMultiplayerDrop();
  };

  const deckRef = useRef(null);
  const discardRef = useRef(null);
  const circleRefs = useRef({});
  const [animations, setAnimations] = useState([]);

  const myCardsRef = useRef([]);
  const savedGroupStructureRef = useRef(null);
  const actionInProgressRef = useRef(false);

  const headerHeight = isMobile ? (isLandscape ? 30 : 35) : 40;
  const socketRef = useRef(socket);
  useEffect(() => { socketRef.current = socket; }, [socket]);

  // ==================== LANDSCAPE + FULLSCREEN (YouTube-style) ====================
  // The game is landscape-only. On the first user gesture we lock the screen to
  // landscape and request fullscreen so the browser chrome (address bar etc.) hides,
  // exactly like a fullscreen video player.
  // Chrome/Android only allows screen.orientation.lock() WHILE in fullscreen, so we must enter
  // fullscreen FIRST and lock afterwards. Both need a user gesture, so we also retry on taps until
  // the device really is landscape. iOS Safari has no lock API -> we show a "rotate" overlay.
  const [needsRotate, setNeedsRotate] = useState(false);
  const isPortraitNow = () => window.innerHeight > window.innerWidth;
  const tryLockLandscape = useCallback(async () => {
    if (!isMobile) return;
    try {
      const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      if (!inFs && fullscreenService && typeof fullscreenService.enterFullscreen === 'function') {
        await fullscreenService.enterFullscreen(document.documentElement);
      }
    } catch (e) { /* fullscreen may be refused without a gesture */ }
    try {
      const so = window.screen && window.screen.orientation;
      if (so && typeof so.lock === 'function') await so.lock('landscape');
    } catch (e) { /* unsupported (iOS) or refused */ }
    setNeedsRotate(isMobile && isPortraitNow());
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return undefined;
    // Try straight away (works inside the Capacitor app / installed PWA), then on every
    // user gesture until the screen is landscape.
    tryLockLandscape();
    const onGesture = () => { if (isPortraitNow() || !document.fullscreenElement) tryLockLandscape(); };
    const onResize = () => setNeedsRotate(isPortraitNow());
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('touchend', onGesture);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('touchend', onGesture);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      try { const so = window.screen && window.screen.orientation; if (so && so.unlock) so.unlock(); } catch (e) { /* ignore */ }
    };
  }, [isMobile, tryLockLandscape]);

  const submissionStartInfoRef = useRef({ startTimestamp: null, duration: null });

  // ==================== SEATING ====================
  const buildSeatingPlayers = useCallback((seatingCards, allPlayers) => {
    if (!Array.isArray(seatingCards)) return [];
    return seatingCards.map(sc => {
      const playerInfo = allPlayers.find(p => String(p.userId) === String(sc.userId));
      const username = playerInfo?.username || 'Player';
      const card = {
        id: `seating_spade_${sc.rank}`,
        suit: sc.suit || 'spades',
        rank: sc.rank,
        value: 0,
        isRed: false,
        isJoker: false,
        isSeatingCard: true
      };
      return { userId: sc.userId, username, card };
    });
  }, []);

  const activateSeatingPhase = useCallback((seatingCards, countdown, firstPlayerId, allPlayers, seatingOrder, positions) => {
    const players = buildSeatingPlayers(seatingCards, allPlayers);
    setSeatingData({ players, firstPlayerId, countdown, seatingOrder, positions, seatingCards });
    setSeatingCountdown(countdown);
    setSeatingPhase(true);
    if (seatingTimerRef.current) clearInterval(seatingTimerRef.current);
    if (countdown > 0) {
      const timer = setInterval(() => {
        setSeatingCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            seatingTimerRef.current = null;
            setSeatingPhase(false);
            socketRef.current?.emit('request-game-state', { gameId: actualGameId });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      seatingTimerRef.current = timer;
    } else {
      seatingTimerRef.current = null;
      setSeatingPhase(false);
      socketRef.current?.emit('request-game-state', { gameId: actualGameId });
    }
  }, [actualGameId, buildSeatingPlayers]);

  const deactivateSeating = useCallback(() => {
    if (seatingTimerRef.current) {
      clearInterval(seatingTimerRef.current);
      seatingTimerRef.current = null;
    }
    setSeatingPhase(false);
  }, []);

  // 🏁 FIX: Only redirect to lobby after point table is closed (for game-over scenario).
  // When the game ends, we show the point table first. The redirect timer only starts
  // AFTER the user closes the point table (via handleClosePointTable which sets gameEnded=true).
  // This avoids the race where the redirect timer starts before the point table is shown.
  const lobbyRedirectTimeoutRef = useRef(null);
  useEffect(() => {
    // Only redirect when game is truly finished AND point table is NOT showing AND game is declared ended
    // The point table must be dismissed first (via handleClosePointTable) before redirecting.
    if (gameEnded && multiplayerGameStatus === 'finished' && !showPointTable && !seatingPhase) {
      console.log('🏁 Game ended, point table closed — redirecting to lobby in 3s');
      lobbyRedirectTimeoutRef.current = setTimeout(() => {
        handleRedirectToLobby();
      }, 3000);
      return () => {
        if (lobbyRedirectTimeoutRef.current) {
          clearTimeout(lobbyRedirectTimeoutRef.current);
          lobbyRedirectTimeoutRef.current = null;
        }
      };
    }
  }, [gameEnded, multiplayerGameStatus, showPointTable, seatingPhase]);

  const handleCardSelect = (cardId) => {
    setSelectedCardsForGroup(prev => {
      if (prev.includes(cardId)) return prev.filter(id => id !== cardId);
      return [...prev, cardId];
    });
  };

  const handleAutoArrange = () => handleMultiplayerAutoArrange();
  const handleCreateGroup = () => handleLocalGroup();
  const handleResetGroupSelection = () => {
    setSelectedCardsForGroup([]);
    setShowGroupButton(false);
  };

  const handleDeclaration = () => {
    if (!hasDrawnThisTurnRef.current) { setErrorMessage('You must draw a card first'); return; }
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (actionInProgressRef.current) { setErrorMessage('Please wait for current action to complete'); return; }
    if (selectedCardsForGroup.length !== 1) {
      setErrorMessage('Select exactly 1 card to discard when declaring');
      return;
    }
    setShowDeclareConfirm(true);
  };

  const handleDeclareConfirm = async () => {
    setShowDeclareConfirm(false);
    await handleMultiplayerDeclaration();
  };

  const handleDeclareCancel = () => setShowDeclareConfirm(false);
  const handleDiscard = () => handleMultiplayerDiscard();
  const handleDrop = () => handleMultiplayerDrop();

  const addDrawnCardToHand = (drawnCard, serverHand = null) => {
    SoundUtils.play('cardDraw');
    let currentCards = [...myCardsRef.current];

    // 🎴 RECONCILE WITH SERVER: if the local structure is out of sync with the server hand
    // (e.g. stale/empty refs after a reconnect or a missed update), the player could end up
    // seeing only the drawn card. In that case rebuild from the authoritative server hand.
    const flattenIds = (items) => {
      const ids = [];
      (items || []).forEach(item => {
        if (item?.isGroup) (item.cards || []).forEach(c => ids.push(c.id));
        else if (item) ids.push(item.id);
      });
      return ids;
    };
    if (Array.isArray(serverHand) && serverHand.length > 0) {
      const localIds = new Set(flattenIds(currentCards));
      const serverIds = new Set(serverHand.map(c => c.id));
      const localMatchesServer = serverIds.size === localIds.size + (localIds.has(drawnCard.id) ? 0 : 1) &&
        [...localIds].every(id => serverIds.has(id));
      if (!localMatchesServer) {
        console.log('🔄 Local hand out of sync with server after draw — rebuilding from server hand');
        currentCards = serverHand.map(c => ({ ...c }));
        myCardsRef.current = currentCards;
        savedGroupStructureRef.current = currentCards.map(item => ({ ...item }));
        updatePreservedCards(savedGroupStructureRef.current);
        updateLocalGameStateWithMyCards();
        return;
      }
    }
    // Never add the same card twice
    if (flattenIds(currentCards).includes(drawnCard.id)) return;

    let lastGroupIndex = -1;
    for (let i = 0; i < currentCards.length; i++) {
      if (currentCards[i].isGroup) lastGroupIndex = i;
    }
    if (lastGroupIndex !== -1) {
      const updatedGroup = {
        ...currentCards[lastGroupIndex],
        cards: [...currentCards[lastGroupIndex].cards, drawnCard],
        cardCount: currentCards[lastGroupIndex].cards.length + 1
      };
      currentCards[lastGroupIndex] = updatedGroup;
    } else {
      currentCards.push(drawnCard);
    }
    myCardsRef.current = currentCards;
    savedGroupStructureRef.current = currentCards.map(item =>
      item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
    );
    updatePreservedCards(savedGroupStructureRef.current);
    updateLocalGameStateWithMyCards();
  };

  const handleMultiplayerDrawFromDeck = async () => {
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (hasDrawnThisTurnRef.current) return;
    if (actionInProgressRef.current) { setErrorMessage("Please wait for current action to complete"); return; }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const result = await contextDrawCard(true);
      if (result?.success) {
        if (result.drawnCard) addDrawnCardToHand(result.drawnCard, result.myCards);
        hasDrawnThisTurnRef.current = true;
        setHasDrawnThisTurn(true);
        setSelectedCardsForGroup([]);
        setShowGroupButton(false);
        setIsArrangeUsed(false);
      } else if (result?.error && result.error !== 'You have already drawn a card this turn') {
        setErrorMessage(result.error || "Failed to draw card");
      }
    } catch (err) {
      if (err.message !== 'You have already drawn a card this turn') {
        setErrorMessage(err.message || "Error drawing card");
      }
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const handleMultiplayerDrawFromDiscard = async () => {
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (hasDrawnThisTurnRef.current) return;
    if (actionInProgressRef.current) { setErrorMessage("Please wait for current action to complete"); return; }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const result = await contextDrawCard(false);
      if (result?.success) {
        if (result.drawnCard) addDrawnCardToHand(result.drawnCard, result.myCards);
        hasDrawnThisTurnRef.current = true;
        setHasDrawnThisTurn(true);
        setSelectedCardsForGroup([]);
        setShowGroupButton(false);
        setIsArrangeUsed(false);
      } else if (result?.error && result.error !== 'You have already drawn a card this turn') {
        // 🃏 Show friendly message for printed joker draw attempt (non-exception path)
        if (result.error === 'Cannot draw a printed joker from the discard pile') {
          setErrorMessage('❌ You cannot take a printed joker from the discard pile');
        } else {
          setErrorMessage(result.error || "Failed to draw from discard");
        }
      }
    } catch (err) {
      if (err.message !== 'You have already drawn a card this turn') {
        // 🃏 Show friendly message for printed joker draw attempt
        if (err.message === 'Cannot draw a printed joker from the discard pile') {
          setErrorMessage('❌ You cannot take a printed joker from the discard pile');
        } else {
          setErrorMessage(err.message || "Error drawing from discard");
        }
      }
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const handleMultiplayerDiscard = async () => {
    if (!hasDrawnThisTurnRef.current) { setErrorMessage("You must draw a card first"); return; }
    if (selectedCardsForGroup.length !== 1) { setErrorMessage("Select exactly one card to discard"); return; }
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (actionInProgressRef.current) { setErrorMessage("Please wait for current action to complete"); return; }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const cardId = selectedCardsForGroup[0];
      const structureBeforeDiscard = myCardsRef.current.map(item =>
        item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
      );
      myCardsRef.current = removeCardFromHand(myCardsRef.current, cardId);
      const updatedStructure = removeCardFromHand(structureBeforeDiscard, cardId);
      savedGroupStructureRef.current = updatedStructure;
      updatePreservedCards(updatedStructure);
      updateLocalGameStateWithMyCards();
      setSelectedCardsForGroup([]);
      setShowGroupButton(false);
      setIsArrangeUsed(false);
      const result = await contextDiscardCard(cardId);
      if (!result?.success) {
        setErrorMessage(result?.error || "Failed to discard card");
        if (savedGroupStructureRef.current) {
          myCardsRef.current = savedGroupStructureRef.current;
          updateLocalGameStateWithMyCards();
        }
      }
    } catch (err) {
      setErrorMessage(err.message || "Error discarding card");
      if (savedGroupStructureRef.current) {
        myCardsRef.current = savedGroupStructureRef.current;
        updateLocalGameStateWithMyCards();
      }
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const extractSetsAndRunsFromHand = useCallback(() => {
    const currentCards = myCardsRef.current || [];
    const wildJoker = gameState?.wildJoker;
    const sets = [];
    const runs = [];
    // EVERY group is sent, using the same classification as the server. Before, a group made only of
    // jokers (3 jokers) was silently skipped, so the server saw those cards as "ungrouped" and called the
    // declaration wrong. Sets go as sets; pure/impure sequences (incl. 3+ jokers) go as sequences; the
    // server then decides validity.
    for (const item of currentCards) {
      if (!item.isGroup) continue;
      const cards = item.cards || [];
      if (cards.length === 0) continue;
      if (classifyGroup(cards, wildJoker).kind === 'set') sets.push(cards);
      else runs.push(cards);
    }
    return { sets, runs };
  }, [gameState?.wildJoker]);

  const handleMultiplayerDeclaration = async () => {
    if (!hasDrawnThisTurnRef.current) { setErrorMessage("You must draw a card first"); return; }
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (actionInProgressRef.current) { setErrorMessage("Please wait for current action to complete"); return; }
    if (selectedCardsForGroup.length !== 1) {
      setErrorMessage("Select exactly 1 card to discard when declaring");
      return;
    }
    const discardCardId = selectedCardsForGroup[0];
    actionInProgressRef.current = true;
    setErrorMessage('');
    const structureBefore = myCardsRef.current.map(item =>
      item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
    );
    try {
      const handAfterDiscardRemoval = removeCardFromHand(myCardsRef.current, discardCardId);
      myCardsRef.current = handAfterDiscardRemoval;
      const { sets, runs } = extractSetsAndRunsFromHand();
      const ungroupedCards = (myCardsRef.current || []).filter(item => !item.isGroup);
      if (ungroupedCards.length > 0) {
        setErrorMessage(`You have ${ungroupedCards.length} ungrouped cards. All cards except the discard must be in valid sets or runs.`);
        myCardsRef.current = structureBefore;
        actionInProgressRef.current = false;
        return;
      }
      if (sets.length === 0 && runs.length === 0) {
        setErrorMessage('No valid groups found. Form at least 2 sequences to declare.');
        myCardsRef.current = structureBefore;
        actionInProgressRef.current = false;
        return;
      }
      savedGroupStructureRef.current = handAfterDiscardRemoval.map(item =>
        item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
      );
      updatePreservedCards(savedGroupStructureRef.current);
      updateLocalGameStateWithMyCards();
      setSelectedCardsForGroup([]);
      setShowGroupButton(false);
      const result = await contextDeclareRummy(sets, runs, discardCardId);
      if (!result?.success) {
        // 🛑 WRONG DECLARATION — show prominent popup for 3 seconds
        const currentUsername = currentUser?.username || 'You';
        const errorMsg = result?.error || 'Invalid declaration';
      
        // 🛑 WRONG DECLARATION — Show point table with error explanation
        // if the server returned pointTable (2+ players finished game),
        // otherwise show the wrong declaration popup.
        if (result?.pointTable && result?.pointTable.length > 0 && result?.gameOver) {
          // Game-over wrong declaration (2 players) — show point table immediately
          setWrongDeclarationInfo(null);
          setPointTableData(result.pointTable);
          setPointTableTitle('❌ Invalid Declaration - Point Table');
          setPointTableMessage(errorMsg);
          setIsGameOver(true);
          setShowPointTable(true);
          // Clear local cards to match server state (player is dropped)
          myCardsRef.current = [];
          savedGroupStructureRef.current = [];
          updatePreservedCards([]);
          updateLocalGameStateWithMyCards();
          actionInProgressRef.current = false;
          return;
        }
      
        // For non-game-over wrong declarations (>2 players, game continues):
        setWrongDeclarationInfo({
          message: errorMsg,
          playerName: currentUsername,
          dropped: result?.dropped || false,
          visible: true
        });
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          setWrongDeclarationInfo(null);
        }, 5000);
      
        setErrorMessage(result?.error || "Declaration failed");
        myCardsRef.current = structureBefore;
        savedGroupStructureRef.current = structureBefore;
        updatePreservedCards(structureBefore);
        updateLocalGameStateWithMyCards();
      }
    } catch (err) {
      const errorMsg = err.message || "Error declaring";
      setErrorMessage(errorMsg);
      // 🛑 BUG FIX: On wrong declaration, do NOT restore cards.
      // The server has already cleared the hand and marked the player as dropped.
      // Restoring cards would override the server's dropped state.
      // Show the point table with the error explanation (if game ended),
      // otherwise show the wrong declaration popup.
      const droppedPhrases = ['wrong declaration', '80 penalty', 'dropped', 'invalid declaration'];
      const isWrongDecl = droppedPhrases.some(p => errorMsg.toLowerCase().includes(p));
      if (isWrongDecl) {
        const currentUsername = currentUser?.username || 'You';
        // Try to show point table if game-over data was sent via rummy-declared event
        // The handleRummyDeclared handler will pick it up and show the point table.
        // But also set the error message as point table message if pointTable is already available.
        setWrongDeclarationInfo({
          message: errorMsg,
          playerName: currentUsername,
          dropped: true,
          visible: true
        });
        // Show point table briefly delayed — the rummy-declared event should arrive
        // almost simultaneously and set the actual point table.
        setTimeout(() => setWrongDeclarationInfo(null), 3000);
        // Clear local cards to match server state
        myCardsRef.current = [];
        savedGroupStructureRef.current = [];
        updatePreservedCards([]);
        updateLocalGameStateWithMyCards();
      } else {
        // For NON-wrong-declaration errors (connection issues, etc.), restore cards
        myCardsRef.current = structureBefore;
        savedGroupStructureRef.current = structureBefore;
        updatePreservedCards(structureBefore);
        updateLocalGameStateWithMyCards();
      }
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const handleMultiplayerDrop = async () => {
    if (!contextIsMyTurn) { setErrorMessage("It's not your turn!"); return; }
    if (actionInProgressRef.current) { setErrorMessage("Please wait for current action to complete"); return; }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const result = await contextDropFromGame();
      if (result?.success) {
        setSelectedCardsForGroup([]);
        setShowGroupButton(false);
        setIsArrangeUsed(false);
      } else {
        setErrorMessage(result?.error || "Failed to drop");
      }
    } catch (err) {
      setErrorMessage(err.message || "Error dropping");
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const handleLocalGroup = () => {
    if (selectedCardsForGroup.length < 2) {
      setErrorMessage('Select at least 2 cards to form a group');
      return;
    }
    if (actionInProgressRef.current) {
      setErrorMessage('Please wait for current action to complete');
      return;
    }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const currentCards = myCardsRef.current;
      const result = createGroupFromSelectedCards(selectedCardsForGroup, currentCards, 6);
      if (result.success) {
        let updatedCards = result.updatedCards;
        updatedCards = cleanupSingleCardGroups(updatedCards);
        myCardsRef.current = updatedCards;
        savedGroupStructureRef.current = updatedCards.map(item =>
          item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
        );
        updatePreservedCards(savedGroupStructureRef.current);
        updateLocalGameStateWithMyCards();
        setSelectedCardsForGroup([]);
        setErrorMessage('');
      } else {
        setErrorMessage(result.message);
      }
    } catch (err) {
      setErrorMessage(err.message || 'Error creating group');
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const handleMoveCard = (cardId, sourceGroupId, targetGroupId, targetIndex = -1) => {
    let currentCards = [...myCardsRef.current];
    let cardToMove = null;
    if (sourceGroupId) {
      const srcGroupIdx = currentCards.findIndex(c => c.isGroup && c.id === sourceGroupId);
      if (srcGroupIdx !== -1) {
        const group = currentCards[srcGroupIdx];
        const cardIdx = group.cards.findIndex(c => c.id === cardId);
        if (cardIdx !== -1) {
          cardToMove = group.cards[cardIdx];
          const newGroupCards = group.cards.filter((_, idx) => idx !== cardIdx);
          if (newGroupCards.length === 0) {
            currentCards.splice(srcGroupIdx, 1);
          } else {
            currentCards[srcGroupIdx] = { ...group, cards: newGroupCards, cardCount: newGroupCards.length };
          }
        }
      }
    } else {
      const cardIdx = currentCards.findIndex(c => c.id === cardId && !c.isGroup);
      if (cardIdx !== -1) {
        cardToMove = currentCards[cardIdx];
        currentCards.splice(cardIdx, 1);
      }
    }
    if (!cardToMove) return;
    let createdGroupId = null;
    const groupCount = currentCards.filter(c => c.isGroup).length;
    if (!targetGroupId && targetIndex === -1) {
      if (groupCount < 6) {
        createdGroupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        currentCards.push({
          isGroup: true, cards: [cardToMove],
          id: createdGroupId,
          groupType: 'manual', isValid: true, createdAt: Date.now(), cardCount: 1
        });
      } else {
        const lastGroupIndex = currentCards.findLastIndex(c => c.isGroup);
        if (lastGroupIndex !== -1) {
          currentCards[lastGroupIndex].cards.push(cardToMove);
          currentCards[lastGroupIndex].cardCount = currentCards[lastGroupIndex].cards.length;
        } else {
          currentCards.push(cardToMove);
        }
      }
    } else if (targetGroupId && targetIndex === -1) {
      const tgtGroupIdx = currentCards.findIndex(c => c.isGroup && c.id === targetGroupId);
      if (tgtGroupIdx !== -1) {
        currentCards[tgtGroupIdx].cards.push(cardToMove);
        currentCards[tgtGroupIdx].cardCount = currentCards[tgtGroupIdx].cards.length;
      } else {
        currentCards.push(cardToMove);
      }
    } else {
      if (targetGroupId) {
        const tgtGroupIdx = currentCards.findIndex(c => c.isGroup && c.id === targetGroupId);
        if (tgtGroupIdx !== -1) {
          currentCards[tgtGroupIdx].cards.splice(targetIndex, 0, cardToMove);
          currentCards[tgtGroupIdx].cardCount = currentCards[tgtGroupIdx].cards.length;
        } else {
          currentCards.push(cardToMove);
        }
      } else {
        currentCards.splice(targetIndex, 0, cardToMove);
      }
    }
    // If the group the card came from is left with < 2 cards (or only 1 loose card remains),
    // add the remaining card to the last group so the hand takes less space.
    currentCards = cleanupSingleCardGroups(currentCards, {
      sourceGroupId: sourceGroupId && sourceGroupId !== targetGroupId ? sourceGroupId : null,
      protectedGroupIds: createdGroupId ? [createdGroupId] : []
    });
    myCardsRef.current = currentCards;
    savedGroupStructureRef.current = currentCards.map(item =>
      item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
    );
    updatePreservedCards(savedGroupStructureRef.current);
    updateLocalGameStateWithMyCards();
  };

  const handleMultiplayerAutoArrange = async () => {
    if (actionInProgressRef.current) {
      setErrorMessage("Please wait for current action to complete");
      return;
    }
    actionInProgressRef.current = true;
    setErrorMessage('');
    try {
      const currentPlayer = getCurrentPlayer();
      if (!currentPlayer?.cards?.length) throw new Error('No cards to arrange');
      const arranged = autoArrangeCards(currentPlayer.cards, gameState?.wildJoker);
      myCardsRef.current = arranged.arrangedCards;
      savedGroupStructureRef.current = arranged.arrangedCards.map(item =>
        item.isGroup ? { ...item, cards: [...item.cards] } : { ...item }
      );
      updatePreservedCards(savedGroupStructureRef.current);
      updateLocalGameStateWithMyCards();
      setIsArrangeUsed(true);
      setSelectedCardsForGroup([]);
      setShowGroupButton(false);
    } catch (err) {
      setErrorMessage(err.message || 'Error auto-arranging');
    } finally {
      actionInProgressRef.current = false;
    }
  };

  const getCurrentPlayer = () => {
    if (!currentUser) return null;
    const state = localGameState || multiplayerGameState;
    const playerInGameState = state?.players?.find(p =>
      String(p.userId) === String(currentUser.id) || String(p.id) === String(currentUser.id)
    );
    if (playerInGameState) {
      const cards = playerInGameState.cards?.length ? playerInGameState.cards : myCardsRef.current;
      return { ...playerInGameState, cards, id: playerInGameState.userId || playerInGameState.id };
    }
    if (myCardsRef.current.length > 0) {
      return { userId: currentUser.id, id: currentUser.id, username: currentUser.username, cards: myCardsRef.current, position: 'bottom', isCurrentPlayer: true };
    }
    return null;
  };

  // ===================== SUBMISSION =====================
  const handleSubmitArrangement = useCallback(async () => {
    if (actionInProgressRef.current || hasSubmittedRef.current) return;
    actionInProgressRef.current = true;
    setErrorMessage('');
    setHasSubmitted(true);
    hasSubmittedRef.current = true;
    try {
      const { sets, runs } = extractSetsAndRunsFromHand();
      // DEBUG: Check socket state before emitting
      const sock = socketRef.current;
      console.log('🔍 SUBMIT DEBUG:', {
        hasSocket: !!sock,
        socketConnected: sock?.connected,
        socketId: sock?.id,
        actualGameId,
        setsCount: sets.length,
        runsCount: runs.length,
        hasSubmittedRef: hasSubmittedRef.current,
      });
      if (!sock) {
        console.error('❌ SUBMIT FAILED: socketRef.current is null!');
        setErrorMessage('Connection lost. Please refresh.');
        actionInProgressRef.current = false;
        hasSubmittedRef.current = false;
        setHasSubmitted(false);
        return;
      }
      if (!sock.connected) {
        console.error('❌ SUBMIT FAILED: socket not connected!');
        setErrorMessage('Connection lost. Please wait...');
        // Keep hasSubmitted true so user doesn't click again
        // The socket.io buffer will send it when reconnected
      }
      // Add a callback timeout to detect silent failures
      let callbackFired = false;
      const callbackTimeout = setTimeout(() => {
        if (!callbackFired) {
          console.error('❌ SUBMIT TIMEOUT: Server never acknowledged submission');
          console.error('   Socket state:', { id: sock.id, connected: sock.connected, disconnected: sock.disconnected });
          setErrorMessage('Server not responding. Please try again.');
          actionInProgressRef.current = false;
          hasSubmittedRef.current = false;
          setHasSubmitted(false);
        }
      }, 10000);

      const emitResult = sock.emit('submit-arrangement', {
        gameId: actualGameId,
        sets,
        runs,
      }, (response) => {
        callbackFired = true;
        clearTimeout(callbackTimeout);
        if (response?.error) {
          actionInProgressRef.current = false;
          setErrorMessage(response.error);
          setHasSubmitted(false);
          hasSubmittedRef.current = false;
          return;
        }
        console.log('📋 Submission accepted by server');
        console.log('📊 PointTable from submit callback:', JSON.stringify(response.pointTable.map(p => ({
          user: p.username?.substring(0, 6),
          pts: p.pointsThisGame,
          cum: p.cumulativeScore,
          sub: p.submitted
        }))));
        // Instantly show point table using the data from callback
        if (response.pointTable) {
          setPointTableData(response.pointTable);
          setShowPointTable(true);
          const submittedCount = response.pointTable.filter(p => p.submitted).length;
          const total = response.pointTable.length;
          const remaining = total - submittedCount;
          if (remaining > 0) {
            setPointTableMessage(`${submittedCount}/${total} submitted. Waiting for ${remaining} more...`);
          } else {
            setPointTableMessage('All players submitted!');
          }
        }
        console.log('🔕 NOTIFICATION CLEARED on submit');
        setDeclarationNotification('');
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current);
          autoSubmitTimeoutRef.current = null;
        }
      });
    } catch (err) {
      setErrorMessage(err.message || 'Error submitting arrangement');
      actionInProgressRef.current = false;
    } finally {
      actionInProgressRef.current = false;
    }
  }, [actualGameId, extractSetsAndRunsFromHand]);

  const scheduleAutoSubmit = useCallback((startTimestamp, duration) => {
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    const now = Date.now();
    const elapsed = now - startTimestamp;
    const remaining = Math.max(0, duration - elapsed);
    const fireIn = remaining - 3000; // 3 seconds before server deadline
    if (fireIn <= 0) {
      if (!hasSubmittedRef.current && isSubmissionPhaseRef.current) {
        handleSubmitArrangement();
      }
      return;
    }
    autoSubmitTimeoutRef.current = setTimeout(() => {
      if (!hasSubmittedRef.current && isSubmissionPhaseRef.current) {
        handleSubmitArrangement();
      }
    }, fireIn);
  }, [handleSubmitArrangement]);

  const handleRedirectToLobby = () => {
    if (window.opener) window.close();
    else navigate('/lobby');
  };

  const updateLocalGameStateWithMyCards = useCallback(() => {
    setLocalGameState(prev => {
      const base = prev || multiplayerGameState;
      if (!base?.players) return prev;
      const updatedPlayers = base.players.map(p =>
        String(p.userId) === String(currentUser?.id) ? { ...p, cards: myCardsRef.current } : p
      );
      return { ...base, players: updatedPlayers };
    });
  }, [currentUser, multiplayerGameState]);

  // Submission countdown timer
  useEffect(() => {
    if (!isSubmissionPhase) {
      if (submissionTimerIntervalRef.current) {
        clearInterval(submissionTimerIntervalRef.current);
        submissionTimerIntervalRef.current = null;
      }
      return;
    }
    const updateRemaining = () => {
      const info = submissionStartInfoRef.current;
      if (info.startTimestamp && info.duration) {
        const remaining = Math.max(0, info.duration - (Date.now() - info.startTimestamp));
        setSubmissionTimeLeft(remaining);
        if (remaining <= 0) {
          if (submissionTimerIntervalRef.current) {
            clearInterval(submissionTimerIntervalRef.current);
            submissionTimerIntervalRef.current = null;
          }
        }
      }
    };
    updateRemaining();
    submissionTimerIntervalRef.current = setInterval(updateRemaining, 500);
    return () => {
      if (submissionTimerIntervalRef.current) {
        clearInterval(submissionTimerIntervalRef.current);
        submissionTimerIntervalRef.current = null;
      }
    };
  }, [isSubmissionPhase]);

  useEffect(() => { SoundUtils.init(); }, []);

  // Game sounds (draw / discard / turn) only while a hand is actually being played.
  useEffect(() => {
    SoundUtils.setGameActive(gameState?.status === 'playing');
    return () => SoundUtils.setGameActive(true);
  }, [gameState?.status]);

  // Safety net for the "spinner after a round" problem: if the game is playing but WE have no cards
  // (new deal dealt, hand not delivered yet), keep asking the server for our hand until it arrives.
  const myPlayerForHand = (gameState?.players || []).find(p => String(p.userId) === String(currentUser?.id));
  const needsHand = gameState?.status === 'playing' && !!myPlayerForHand && !myPlayerForHand.dropped &&
    !(myPlayerForHand.cards && myPlayerForHand.cards.length > 0);
  useEffect(() => {
    if (!needsHand || !socket || !socketReady) return undefined;
    let tries = 0;
    const ask = () => socket.emit('request-game-state', { gameId: actualGameId });
    ask();
    const t = setInterval(() => { if (++tries > 8) { clearInterval(t); return; } ask(); }, 1200);
    return () => clearInterval(t);
  }, [needsHand, socket, socketReady, actualGameId]);

  useEffect(() => {
    if (errorMessage) {
      SoundUtils.play('error');
      const t = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(t);
    }
  }, [errorMessage]);

  useEffect(() => { if (multiplayerError) setErrorMessage(multiplayerError); }, [multiplayerError]);

  useEffect(() => {
    if (contextIsMyTurn) {
      SoundUtils.play('turnChange');
      setHasDrawnThisTurn(false);
      hasDrawnThisTurnRef.current = false;
    }
  }, [contextIsMyTurn]);

  useEffect(() => { if (actualGameId) setCurrentGameId(actualGameId); }, [actualGameId, setCurrentGameId]);

  // Leaving a table (or moving to another game id) wipes the shared game state, so a finished
  // game can never leak into the next one as a stale "Game Over" screen.
  const resetGameStateRef = useRef(resetGameState);
  useEffect(() => { resetGameStateRef.current = resetGameState; }, [resetGameState]);
  useEffect(() => () => {
    if (!isPractice && typeof resetGameStateRef.current === 'function') resetGameStateRef.current();
  }, [actualGameId, isPractice]);

  useEffect(() => {
    if (!socket) { setSocketReady(false); return; }
    const onConnect = () => setSocketReady(true);
    if (socket.connected) setSocketReady(true);
    else { setSocketReady(false); socket.on('connect', onConnect); }
    return () => { socket.off('connect', onConnect); };
  }, [socket]);

  const hasJoinedRoomRef = useRef(false);
  useEffect(() => {
    if (!socketReady || !socket) return;
    if (!hasJoinedRoomRef.current) {
      hasJoinedRoomRef.current = true;
      const targetRoomCode = cashGameInfo?.roomCode || roomConfig?.roomCode || gameState?.roomCode;
      socket.emit('join-game', { gameId: actualGameId, roomCode: targetRoomCode });
      socket.emit('request-room-sync', { gameId: actualGameId });
    }
  }, [socketReady, actualGameId, socket, cashGameInfo, roomConfig, gameState?.roomCode]);

  // 🛑 Auto Drop is a persistent MODE owned by the server: it drops this player at the start of every
  // turn (every round) until they switch it off. The server also switches it off when the next drop would
  // eliminate them, so the button state is simply mirrored from the game state here.
  const myAutoDropOn = gameState?.players?.find(p => String(p.userId) === String(currentUser?.id))?.autoDropQueued;
  const prevAutoDropOnRef = useRef(false);
  useEffect(() => {
    if (myAutoDropOn === undefined) return;
    if (prevAutoDropOnRef.current && !myAutoDropOn) {
      // switched off by the server (not by the player) because dropping would eliminate them
      const block = getDropBlock();
      if (block && isAutoDropQueued) setDropNotice(block);
    }
    prevAutoDropOnRef.current = !!myAutoDropOn;
    setIsAutoDropQueued(!!myAutoDropOn);
  }, [myAutoDropOn]);

  // Countdown timer interval for waiting room
  useEffect(() => {
    if (!roomCountdown || roomCountdown <= 0) return;
    const t = setInterval(() => {
      setRoomCountdown(prev => (prev && prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [roomCountdown]);

  const stateRequestSentRef = useRef(false);
  const currentUserIdRef = useRef(currentUser?.id);
  useEffect(() => { currentUserIdRef.current = currentUser?.id; }, [currentUser?.id]);

  // 🔧 DEFENSIVE SYNC: keep hasDrawnThisTurn in lockstep with the server state.
  // This is a React-state-based fallback (in addition to the socket event handler)
  // so the Declare/Discard buttons always activate right after a draw, even if a
  // game-state-update socket event was missed/delayed during rapid AI turns.
  useEffect(() => {
    const myPlayer = multiplayerGameState?.players?.find(p =>
      String(p.userId) === String(currentUser?.id) || String(p.id) === String(currentUser?.id)
    );
    if (myPlayer && myPlayer.hasDrawnThisTurn !== undefined) {
      if (myPlayer.hasDrawnThisTurn !== hasDrawnThisTurnRef.current) {
        hasDrawnThisTurnRef.current = myPlayer.hasDrawnThisTurn;
        setHasDrawnThisTurn(myPlayer.hasDrawnThisTurn);
      }
    }
  }, [multiplayerGameState, currentUser?.id]);
  // Track the previous game status to detect transitions (e.g. finished→playing for new deal)
  const prevGameStatusRef = useRef(null);

  useEffect(() => {
    if (!socketReady || !socket) return;
    let stateRequestTimeout;

    // ---- CENTRALISED STATE HANDLER ----
    const handleGameStateUpdate = (data) => {
      if (!data.gameState) return;
      const newState = data.gameState;
      if (newState.status && newState.status !== 'waiting') setLeftWaiting(true);
      console.log('🔄 game-state-update:', { status: newState.status, pointTableLen: newState.pointTable?.length });

      // Seating phase
      if (newState.seatingPhase && newState.status === 'seating') {
        const cards = newState.seatingPhase.seatingCards || [];
        const countdown = Math.max(1, typeof newState.seatingPhase.countdown === 'number' ? Math.ceil(newState.seatingPhase.countdown / 1000) : 15);
        activateSeatingPhase(cards, countdown, newState.seatingPhase.firstPlayer || newState.seatingPhase.firstPlayerId, newState.players || [], newState.seatingPhase.seatingOrder, newState.seatingPhase.positions);
      }

      // 🔧 FIX: Sync hasDrawnThisTurn from server on game-state-update
      if (newState.players && currentUserIdRef.current) {
        const myPlayerData = newState.players.find(p =>
          String(p.userId) === String(currentUserIdRef.current) || String(p.id) === String(currentUserIdRef.current)
        );
        if (myPlayerData && myPlayerData.hasDrawnThisTurn !== undefined) {
          const serverDrawn = myPlayerData.hasDrawnThisTurn;
          if (serverDrawn !== hasDrawnThisTurnRef.current) {
            console.log('🔄 Sync hasDrawnThisTurn from game-state-update:', serverDrawn);
            hasDrawnThisTurnRef.current = serverDrawn;
            setHasDrawnThisTurn(serverDrawn);
          }
        }
        
        // 🛑 BUG FIX: Only merge local cards if the player is NOT dropped.
        // When a player is dropped (wrong declaration), the server has already cleared the hand.
        // Overriding with local cards would resurrect cards that should be gone.
        const myNewPlayer = newState.players.find(p =>
          String(p.userId) === String(currentUserIdRef.current) || String(p.id) === String(currentUserIdRef.current)
        );
        const isServerDropped = myNewPlayer?.dropped === true;
        
        // 🎴 CRITICAL FIX: When a NEW deal starts (transition INTO 'playing' from
        // 'finished'/'submission'/'seating'), clear old card refs so the merge below
        // doesn't overwrite the server's fresh 13-card hand with the OLD hand.
        // We track prevGameStatusRef to ONLY clear on transitions, not on every
        // game-state-update during normal play (which would wipe refs mid-game).
        const prevStatus = prevGameStatusRef.current;
        const isNewDealTransition = newState.status === 'playing' &&
          prevStatus !== null && prevStatus !== 'playing';
        if (isNewDealTransition) {
          console.log('🔄 New deal started (transition: ' + prevStatus + ' → playing) — clearing old card refs');
          // The broadcast state hides every hand, so ask the server for OUR new hand right away.
          // (Without this the table waited forever for cards and showed only a spinner.)
          socket.emit('request-game-state', { gameId: actualGameId });
          myCardsRef.current = [];
          savedGroupStructureRef.current = [];
          updatePreservedCards([]);
        }
        // Update the previous status tracker AFTER the transition logic
        prevGameStatusRef.current = newState.status;
        
        if (!isServerDropped) {
          const src = savedGroupStructureRef.current || myCardsRef.current;
          if (src.length > 0) {
            newState.players = newState.players.map(p =>
              String(p.userId) === String(currentUserIdRef.current) ? { ...p, cards: src } : p
            );
          }
        } else {
          // Also clear local refs to match server state
          console.log('🛑 Player is dropped — clearing local card refs to match server');
          myCardsRef.current = [];
          savedGroupStructureRef.current = [];
          updatePreservedCards([]);
        }
      }

      // Update local game state
      if (newState.status === 'submission' || newState.status === 'finished') {
        setLocalGameState(prev => {
          if (!prev?.players) return { ...prev, ...newState };
          const mergedPlayers = newState.players.map(newP => {
            const oldP = prev.players.find(
              p => String(p.userId) === String(newP.userId) || String(p.id) === String(newP.id)
            );
            if (!oldP) return newP;
            const cards = newP.cards?.length > 0 ? newP.cards : (oldP.cards || []);
            return { ...oldP, ...newP, cards, cardCount: cards.length };
          });
          return { ...prev, ...newState, players: mergedPlayers };
        });
      } else {
        setLocalGameState(prev => {
          if (!prev?.players) return { ...prev, ...newState };
          // Deep merge: preserve existing player fields and merge with new state
          const mergedPlayers = newState.players ? newState.players.map(newP => {
            const oldP = prev.players.find(
              p => String(p.userId) === String(newP.userId) || String(p.id) === String(newP.id)
            );
            return oldP ? { ...oldP, ...newP } : newP;
          }) : prev.players;
          return { ...prev, ...newState, players: mergedPlayers };
        });
      }

      // Submission phase handling
      if (newState.status === 'submission' && newState.submission) {
        // 🛑 FIX: Detect if this is a NEW submission phase for a different deal.
        // When a new deal starts after a previous deal's submission,
        // isSubmissionPhase may still be true from the previous deal.
        // We need to force-reset it so the countdown timer useEffect re-runs
        // with the new startTimestamp.
        const prevStartInfo = submissionStartInfoRef.current;
        const newStartInfo = newState.submission;
        if (isSubmissionPhaseRef.current && prevStartInfo && newStartInfo &&
            prevStartInfo.startTimestamp !== newStartInfo.startTimestamp) {
          console.log('🔄 New submission phase detected — resetting submission timer');
          setIsSubmissionPhase(false);
          isSubmissionPhaseRef.current = false;
          setHasSubmitted(false);
          hasSubmittedRef.current = false;
          // Small timeout to ensure React processes the reset before setting again
          setTimeout(() => {
            // The next game-state-update or the re-set below will set it back
          }, 0);
        }
        const sub = newState.submission;
        const isDeclarer = String(sub.declaredPlayer) === String(currentUserIdRef.current);
        setIsSubmissionPhase(true);
        isSubmissionPhaseRef.current = true;

        submissionStartInfoRef.current = {
          startTimestamp: sub.startTimestamp,
          duration: sub.duration
        };

        if (isDeclarer && !hasSubmittedRef.current) {
          setHasSubmitted(true);
          hasSubmittedRef.current = true;
        } else if (!isDeclarer && !hasSubmittedRef.current) {
          // Show declaration notification for non-declarers
          const declarerName = newState.players.find(p => String(p.userId) === String(sub.declaredPlayer))?.username || 'A player';
          setDeclarationNotification(`♠️ ${declarerName} has declared! Arrange your cards and submit within 1 minute.`);
           console.log('🔔 NOTIFICATION SET for non-declarer', currentUserIdRef.current, 'at', Date.now());
        }

        if (!hasSubmittedRef.current && sub.startTimestamp && sub.duration) {
          scheduleAutoSubmit(sub.startTimestamp, sub.duration);
        }

        // 📊 POINT TABLE DURING SUBMISSION:
        // Show for declarer immediately (auto-submitted by server).
        // Show for non-declarer after they submit (via submit-arrangement callback).
        // Show for ALL players when everyone has submitted (countdown is running).
        if (newState.pointTable && newState.pointTable.length > 0) {
          setPointTableData(newState.pointTable);
          const submittedCount = newState.pointTable.filter(p => p.submitted).length;
          const total = newState.pointTable.length;
          const remaining = total - submittedCount;
          
          // 🕐 Track countdown for display in PointTableModal
          const cd = typeof sub.countdown === 'number' ? sub.countdown : null;
          
          // 🛑 FIX: Use a local timer as fallback so the countdown still ticks
          // even if game-state-update events are delayed/missed.
          if (cd !== null) {
            setPointTableCountdown(cd);
          } else if (remaining === 0 && pointTableCountdown === null) {
            // Countdown might start at 10 on next event
          }
          
          if (isDeclarer) {
            // Declarer was auto-submitted by server → show point table right away
            setShowPointTable(true);
            setPointTableTitle('📊 Point Table');
            if (remaining > 0) {
              setPointTableMessage(`${submittedCount}/${total} submitted. Waiting for ${remaining} more...`);
            } else {
              // Show countdown when all submitted
              if (cd !== null && cd > 0) {
                setPointTableMessage(`All players submitted! Finalizing in ${cd}s...`);
              } else {
                setPointTableMessage('All players submitted! Finalizing...');
              }
            }
            // Clear any existing notification for declarer
            setDeclarationNotification('');
          } else if (remaining === 0) {
            // 🎯 ALL PLAYERS SUBMITTED — Show point table for everyone
            // (non-declarers see countdown and can click 'Next Deal')
            setShowPointTable(true);
            setPointTableTitle('📊 Point Table - All Submitted');
            if (cd !== null && cd > 0) {
              setPointTableMessage(`All players submitted! Finalizing in ${cd}s...`);
            } else {
              setPointTableMessage('All players submitted! Finalizing...');
            }
            setDeclarationNotification(''); 
          }
        }
      } else if (newState.status !== 'submission') {
        if (isSubmissionPhaseRef.current) {
          setIsSubmissionPhase(false);
          isSubmissionPhaseRef.current = false;
          setHasSubmitted(false);
          hasSubmittedRef.current = false;
          setDeclarationNotification('');
          setPointTableCountdown(null);
          if (autoSubmitTimeoutRef.current) {
            clearTimeout(autoSubmitTimeoutRef.current);
            autoSubmitTimeoutRef.current = null;
          }
        }
      }


      
      // 🚀 AUTO-CLOSE point table when a new deal starts (status changes to 'playing')
      // The server auto-starts the next deal after 10 seconds in finalizeSubmissionPhase.
      // When the new deal begins, the game-state-update with status 'playing' arrives.
      // Close the point table so the player can play the new deal.
      if (newState.status === 'playing' && showPointTable && !isGameOver) {
        console.log('🔄 New deal started — auto-closing point table');
        setShowPointTable(false);
        setPointTableCountdown(null);
        // Reset submission-related state for the new deal
        setIsSubmissionPhase(false);
        isSubmissionPhaseRef.current = false;
        setHasSubmitted(false);
        hasSubmittedRef.current = false;
        setHasDrawnThisTurn(false);
        hasDrawnThisTurnRef.current = false;
        setDeclarationNotification('');
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current);
          autoSubmitTimeoutRef.current = null;
        }
        
        // 🎴 CRITICAL FIX: Clear old card refs so they don't override the server's new 13-card hand.
        // The playerRefs (myCardsRef, savedGroupStructureRef) still hold the OLD hand from the previous deal.
        // When the merge logic below runs (the !isServerDropped block), it will overwrite the server's
        // fresh cards with old cards, making it look like the old game state remains.
        // We must clear them here so the server's new hand from getPublicGameState() is used.
        myCardsRef.current = [];
        savedGroupStructureRef.current = [];
        updatePreservedCards([]);
      }
      
      // 🏁 FIX: Show point table when game-state-update has status 'finished'.
      // This handles cases where the game ended without a 'rummy-declared' event
      // (e.g., player drop ends the game, all players dropped, etc.).
      // The 'game-ended' event should also fire simultaneously with pointTable data.
      if (newState.status === 'finished' && newState.pointTable && newState.pointTable.length > 0 && !showPointTable && !gameEnded) {
        console.log('🏁 game-state-update shows finished with pointTable — showing point table');
        setPointTableData(newState.pointTable);
        setPointTableTitle('📊 Game Over - Point Table');
        setIsGameOver(true);
        setGameEnded(true);
        setShowPointTable(true);
      }

      // 📦 FIX: Show point table when game-state-update has status 'deal_completed'.
      // This handles the case where a drop in deal rummy ends just the current deal
      // (not the entire game). The server emits 'rummy-declared' with gameOver=false,
      // but as a fallback, also check game-state-update data.
      if (newState.status === 'deal_completed' && newState.pointTable && newState.pointTable.length > 0 && !showPointTable) {
        console.log('📦 game-state-update shows deal_completed with pointTable — showing point table');
        setPointTableData(newState.pointTable);
        // ?? POINTS RUMMY: Don't show deal numbers — use "Round" instead
        const isPointsRummy = newState.gameMode === 'points';
        setPointTableTitle(
          isPointsRummy
            ? '📊 Round Complete'
            : `📊 Deal ${newState.currentDeal || '?'}/${newState.totalDeals || '?'} Complete`
        );
        setPointTableMessage(
          isPointsRummy
            ? (newState.declarationMessage || 'Round complete! Next round starting soon...')
            : (newState.declarationMessage || 'Deal completed! Next deal starting soon...')
        );
        setIsGameOver(false);
        setShowPointTable(true);
      }
    };

    const handleSeatingPhaseEvent = (data) => {
      setLeftWaiting(true);
      setRoomCountdown(null);
      // keep waitingRoomData (its players are still needed to draw the table); just stop its timer
      setWaitingRoomData(prev => (prev ? { ...prev, timerRunning: false } : prev));
      const cards = data.seatingCards || [];
      let countdown = data.countdown || 10;
      if (countdown > 60) countdown = Math.max(1, Math.ceil(countdown / 1000));
      const knownPlayers = [...(gameState?.players || []), ...(waitingPlayersRef.current || [])];
      activateSeatingPhase(cards, countdown, data.firstPlayer || data.firstPlayerId, knownPlayers, data.seatingOrder, data.positions);
    };

    const handleGameStarted = (data) => {
      setWaitingRoomData(null);
      setRoomCountdown(null);
      setLeftWaiting(true);
      deactivateSeating();
      const serverState = data.gameState || data;
      if (myCardsRef.current.length > 0 && serverState.players) {
        serverState.players = serverState.players.map(p =>
          String(p.userId) === String(currentUserIdRef.current) ? { ...p, cards: myCardsRef.current } : p
        );
      }
      setLocalGameState(prev => ({ ...prev, ...serverState }));
    };

    const handleGameStateInitial = (data) => {
      if (data.myCards) {
        myCardsRef.current = [...data.myCards];
        savedGroupStructureRef.current = [...data.myCards];
        updatePreservedCards([...data.myCards]);
      }
      // 🔧 FIX: Sync hasDrawnThisTurn from server state on initial load/reconnection
      if (currentUserIdRef.current && data.players) {
        const myPlayerData = data.players.find(p =>
          String(p.userId) === String(currentUserIdRef.current) || String(p.id) === String(currentUserIdRef.current)
        );
        if (myPlayerData && myPlayerData.hasDrawnThisTurn) {
          console.log('🔄 Sync hasDrawnThisTurn=true from game-state-initial');
          hasDrawnThisTurnRef.current = true;
          setHasDrawnThisTurn(true);
        }
      }
      if (data.seatingPhase && data.status === 'seating') {
        const cards = data.seatingPhase.seatingCards || [];
        const countdown = Math.max(1, typeof data.seatingPhase.countdown === 'number' ? Math.ceil(data.seatingPhase.countdown / 1000) : 15);
        activateSeatingPhase(cards, countdown, data.seatingPhase.firstPlayer, data.players || [], data.seatingPhase.seatingOrder, data.seatingPhase.positions);
      }
      if (data.status && data.status !== 'waiting') setLeftWaiting(true);
      setLocalGameState(data);
      if (stateRequestTimeout) clearTimeout(stateRequestTimeout);
    };

    const handleSeatingPhaseEnded = () => {
      deactivateSeating();
      socket.emit('request-game-state', { gameId: actualGameId });
    };

    const handleTurnChanged = () => {};
    const handleGameEndedFromServer = (data) => {
      console.log('🏁 Game ended event:', data);
      setGameEnded(true);
      // Show point table if data includes it (now sent by server)
      if (data?.pointTable && data.pointTable.length > 0) {
        const winnerName = data.winner 
          ? (gameState?.players?.find(p => String(p.userId) === String(data.winner))?.username 
            || data.finalScores?.[data.winner] 
            || 'Player')
          : 'No one';
        const isAllDropped = data.reason === 'all_players_dropped';
        setPointTableData(data.pointTable);
        setPointTableTitle(isAllDropped ? '📊 Game Over - Point Table' : '🏆 Game Over - Final Point Table');
        setPointTableMessage(isAllDropped 
          ? 'All players dropped. Game over!' 
          : `${winnerName} wins!`);
        setIsGameOver(true);
        setShowPointTable(true);
      }
    };
    const handlePlayerDropped = (data) => setErrorMessage(`Player dropped: ${data.penalty} pts`);

    const handleRummyDeclared = (data) => {
      if (data && data.gameOver && data.winner != null) {
        SoundUtils.play(String(data.winner) === String(currentUserIdRef.current) ? 'win' : 'lose');
      }
      if (data.gameStatus === 'submission') {
        // FIX: Don't return early for submission — we need to handle the pointTable
        // that comes with this event, but only show it if appropriate.
        // Actually, during submission phase, the game-state-update event already handles
        // updating the pointTable silently. The rummy-declared for submission does NOT
        // include a pointTable, so we just return safely.
        return;
      }
      setIsSubmissionPhase(false);
      isSubmissionPhaseRef.current = false;
      console.log('🔕 NOTIFICATION CLEARED by rummy-declared event');
      setDeclarationNotification('');
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
      if (data.pointTable) {
        // 🛑 INVALID/WRONG DECLARATION — Show error explanation in point table
        const isInvalid = data.invalid === true;
        const prefix = isInvalid ? '❌ Invalid Declaration' : (data.gameOver ? '🏆 Game Over - Final' : '📊');
        const title = isInvalid
          ? '❌ Invalid Declaration - Point Table'
          : `${prefix} Point Table`;
        setPointTableData(data.pointTable);
        setPointTableTitle(title);
        // Use reason or message from server for invalid declarations
        const baseMsg = data.message || '';
        if (isInvalid && data.reason) {
          // Invalid declarations should show WHY it failed
          setPointTableMessage(
            `❌ ${data.reason}\n\n${baseMsg}`
          );
        } else {
          setPointTableMessage(baseMsg);
        }
        setIsGameOver(data.gameOver !== false);
        setShowPointTable(true);
      }
    };

    const handleCardDiscarded = (data) => {
      SoundUtils.play('cardPlace');   // every player hears every discard
      if (String(data.playerId) === String(currentUserIdRef.current)) {
        myCardsRef.current = removeCardFromHand(myCardsRef.current, data.discardedCard.id);
        if (savedGroupStructureRef.current) {
          savedGroupStructureRef.current = removeCardFromHand(savedGroupStructureRef.current, data.discardedCard.id);
        }
        setLocalGameState(prev => {
          if (!prev?.players) return prev;
          const src = savedGroupStructureRef.current || myCardsRef.current;
          prev.players = prev.players.map(p =>
                        String(p.userId) === String(currentUserIdRef.current) ? { ...p, cards: src } : p
                      );
                      return { ...prev, deckCount: data.deckCount ?? prev.deckCount, wasReshuffled: data.wasReshuffled || false, discardPile: data.discardPile ?? prev.discardPile };
        });
      }
    };

    // 💰 Cash wallet event handlers — refresh wallet on deduction/credit
    const handleCashWalletDebited = (data) => {
      if (String(data.userId) === String(currentUserIdRef.current)) {
        console.log('💰 Wallet debited for cash game — refreshing wallet');
        refreshWallet();
      }
    };
    const handleCashWalletCredited = (data) => {
      if (String(data.userId) === String(currentUserIdRef.current)) {
        console.log('💰 Wallet credited with winnings — refreshing wallet');
        refreshWallet();
      }
    };

    // ===== WAITING ROOM HANDLERS =====
    const handleRoomSync = (data) => {
      console.log('🚪 room-joined/sync in persistent table:', data);
      if (data && data.success) {
        if (data.seatingPhase) { setLeftWaiting(true); return; }
        setWaitingRoomData(prev => ({
          ...prev,
          roomCode: data.roomCode || prev?.roomCode,
          playerCount: data.playerCount !== undefined ? data.playerCount : (prev?.playerCount || 1),
          maxPlayers: data.maxPlayers || prev?.maxPlayers || 6,
          players: data.players || prev?.players || [],
          timerRunning: data.timerStatus ? data.timerStatus.isRunning : false,
          status: 'waiting'
        }));
        if (data.timerStatus && data.timerStatus.isRunning) {
          setRoomCountdown(data.timerStatus.timeRemaining);
        }
      }
    };

    const handlePlayerJoinedWaiting = (data) => {
      console.log('👤 player-joined in persistent table:', data);
      setWaitingRoomData(prev => {
        const currentPlayers = prev?.players || [];
        const exists = currentPlayers.some(p => String(p.userId || p.user_id || p.id) === String(data.userId));
        const updated = exists ? currentPlayers : [...currentPlayers, { userId: data.userId, username: data.username, position: data.seatPosition }];
        return {
          ...prev,
          playerCount: data.playerCount || updated.length,
          maxPlayers: data.maxPlayers || prev?.maxPlayers || 6,
          players: data.players || updated
        };
      });
    };

    const handlePlayerLeftWaiting = (data) => {
      console.log('👤 player-left in persistent table:', data);
      setWaitingRoomData(prev => ({
        ...prev,
        playerCount: Math.max(0, (data.playerCount !== undefined ? data.playerCount : ((prev?.playerCount || 1) - 1))),
        players: (prev?.players || []).filter(p => String(p.userId || p.user_id || p.id) !== String(data.userId))
      }));
    };

    const handleTimerStartedWaiting = (data) => {
      setWaitingRoomData(prev => ({ ...prev, timerRunning: true }));
      setRoomCountdown(data.timeRemaining || 120);
    };

    const handleTimerUpdateWaiting = (data) => {
      setRoomCountdown(data.timeRemaining);
    };

    const handleTimerStoppedWaiting = () => {
      setWaitingRoomData(prev => ({ ...prev, timerRunning: false }));
      setRoomCountdown(null);
    };

    // ----- Custom room: live player list (room code is shared, host starts manually) -----
    const applyCustomRoom = (data) => {
      const room = data && data.room;
      if (!data || !data.success || !room) return;
      setWaitingRoomData(prev => ({
        ...(prev || {}),
        roomCode: room.roomCode || prev?.roomCode,
        creatorId: room.creatorId,
        playerCount: room.playerCount !== undefined ? room.playerCount : (room.players || []).length,
        maxPlayers: room.maxPlayers || prev?.maxPlayers || 6,
        players: (room.players || []).filter(p => !p.leftGame).map(p => ({ userId: p.userId, username: p.username, position: p.seatPosition })),
        timerRunning: false,
        status: prev?.status || 'waiting'
      }));
    };
    const refreshCustomRoom = () => {
      const code = roomConfig?.roomCode;
      if (!code) return;
      socket.emit('get-custom-room', { code }, applyCustomRoom);
    };
    if (roomConfig?.isCustomRoom) {
      socket.on('custom-room-updated', applyCustomRoom);
      socket.on('custom-player-left', refreshCustomRoom);
      refreshCustomRoom();
    }

    socket.on('room-joined', handleRoomSync);
    socket.on('cash-room-joined', handleRoomSync);
    socket.on('player-joined', handlePlayerJoinedWaiting);
    socket.on('cash-player-joined', handlePlayerJoinedWaiting);
    socket.on('player-left', handlePlayerLeftWaiting);
    socket.on('cash-player-left', handlePlayerLeftWaiting);
    socket.on('room-timer-started', handleTimerStartedWaiting);
    socket.on('cash-timer-started', handleTimerStartedWaiting);
    socket.on('room-timer-update', handleTimerUpdateWaiting);
    socket.on('cash-timer-update', handleTimerUpdateWaiting);
    socket.on('room-timer-stopped', handleTimerStoppedWaiting);
    socket.on('cash-timer-stopped', handleTimerStoppedWaiting);

    socket.on('seating-phase-started', handleSeatingPhaseEvent);
    socket.on('game-started', handleGameStarted);
    socket.on('game-state-update', handleGameStateUpdate);
    socket.on('game-state-initial', handleGameStateInitial);
    socket.on('seating-phase-completed', handleSeatingPhaseEnded);
    socket.on('game-ended', handleGameEndedFromServer);
    socket.on('player-dropped', handlePlayerDropped);
    socket.on('card-discarded', handleCardDiscarded);
    socket.on('turn-changed', handleTurnChanged);
    socket.on('rummy-declared', handleRummyDeclared);
    socket.on('cash-wallet-debited', handleCashWalletDebited);
    socket.on('cash-wallet-credited', handleCashWalletCredited);

    if (!stateRequestSentRef.current) {
      stateRequestSentRef.current = true;
      socket.emit('request-game-state', { gameId: actualGameId });
      stateRequestTimeout = setTimeout(() => {
        setErrorMessage('Failed to load game. Please refresh.');
      }, 10000);
    }

    return () => {
      clearTimeout(stateRequestTimeout);
      socket.off('custom-room-updated', applyCustomRoom);
      socket.off('custom-player-left', refreshCustomRoom);
      socket.off('room-joined', handleRoomSync);
      socket.off('cash-room-joined', handleRoomSync);
      socket.off('player-joined', handlePlayerJoinedWaiting);
      socket.off('cash-player-joined', handlePlayerJoinedWaiting);
      socket.off('player-left', handlePlayerLeftWaiting);
      socket.off('cash-player-left', handlePlayerLeftWaiting);
      socket.off('room-timer-started', handleTimerStartedWaiting);
      socket.off('cash-timer-started', handleTimerStartedWaiting);
      socket.off('room-timer-update', handleTimerUpdateWaiting);
      socket.off('cash-timer-update', handleTimerUpdateWaiting);
      socket.off('room-timer-stopped', handleTimerStoppedWaiting);
      socket.off('cash-timer-stopped', handleTimerStoppedWaiting);

      socket.off('seating-phase-started', handleSeatingPhaseEvent);
      socket.off('game-started', handleGameStarted);
      socket.off('game-state-update', handleGameStateUpdate);
      socket.off('game-state-initial', handleGameStateInitial);
      socket.off('seating-phase-completed', handleSeatingPhaseEnded);
      socket.off('game-ended', handleGameEndedFromServer);
      socket.off('player-dropped', handlePlayerDropped);
      socket.off('card-discarded', handleCardDiscarded);
      socket.off('turn-changed', handleTurnChanged);
      socket.off('rummy-declared', handleRummyDeclared);
      socket.off('cash-wallet-debited', handleCashWalletDebited);
      socket.off('cash-wallet-credited', handleCashWalletCredited);
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
    };
  }, [socketReady, socket, actualGameId, activateSeatingPhase, deactivateSeating, scheduleAutoSubmit]);

  // 🤝 Pool Rummy prize split: answer the open offer. The server pays out only when ALL remaining players agree.
  const handleSplitResponse = useCallback((accept) => {
    if (!socket || !actualGameId) return;
    socket.emit('split-response', { gameId: actualGameId, accept: !!accept }, (res) => {
      if (res && res.success === false && res.error) console.warn('Split response failed:', res.error);
    });
  }, [socket, actualGameId]);

  // 🔁 Pool Rummy rejoin: an eliminated player confirmed - the server charges the joining amount and re-seats them.
  const handleRejoin = useCallback(() => new Promise((resolve) => {
    if (!socket || !actualGameId) { resolve({ success: false, error: 'Not connected' }); return; }
    socket.emit('rejoin-game', { gameId: actualGameId }, (res) => resolve(res || { success: false, error: 'No response' }));
  }), [socket, actualGameId]);

  // 🎯 Points Rummy "Play again": leave this finished game and enter the next one straight away.
  //   custom room -> same room code, a new game (first click creates it, the rest join it)
  //   cash game   -> the usual auto-matching for the same amount (2 or 6 players), new players can join too
  const handlePlayAgain = useCallback(() => new Promise((resolve) => {
    if (!socket) { resolve({ success: false, error: 'Not connected' }); return; }
    const goTo = (room, extra) => {
      setShowPointTable(false);
      setGameEnded(false);
      setIsGameOver(false);
      navigate(`/game/${room.id}`, { state: extra, replace: true });
    };
    if (roomConfig?.isCustomRoom) {
      socket.emit('rate-play-again', { gameId: actualGameId }, (res) => {
        if (res && res.success && res.room) {
          goTo(res.room, {
            isCustomRoom: true,
            isCreator: String(res.room.creatorId) === String(currentUser?.id),
            roomCode: res.room.roomCode, gameId: res.room.id, amount: res.room.amount, currency: res.room.currency,
            gameMode: 'rate', maxPlayers: res.room.maxPlayers, playerCount: res.room.playerCount, maxPoints: res.room.points,
          });
          resolve({ success: true });
        } else resolve({ success: false, error: (res && res.error) || 'Could not start a new game' });
      });
      return;
    }
    // cash game: join a new auto-matched game with the same settings
    const onJoined = (data) => {
      // only the reply to THIS join counts (room-sync replies carry no isNewJoin flag)
      if (!data || data.isNewJoin !== true) return;
      socket.off('cash-room-joined', onJoined); socket.off('cash-error', onErr);
      if (data && data.success && data.roomCode) {
        goTo({ id: data.gameId }, {
          isCashGame: true, roomCode: data.roomCode, gameId: data.gameId, gameMode: 'rate',
          entryFee: cashGameInfo?.entryFee, currency: cashGameInfo?.currency, buyInUSD: cashGameInfo?.buyInUSD,
          maxPlayers: data.maxPlayers || cashGameInfo?.maxPlayers, playerCount: data.playerCount, seatPosition: data.seatPosition,
        });
        resolve({ success: true });
      } else resolve({ success: false, error: (data && data.message) || 'Could not start a new game' });
    };
    const onErr = (err) => {
      socket.off('cash-room-joined', onJoined); socket.off('cash-error', onErr);
      resolve({ success: false, error: (err && err.message) || 'Could not start a new game' });
    };
    socket.on('cash-room-joined', onJoined);
    socket.on('cash-error', onErr);
    socket.emit('join-cash-rummy', {
      gameMode: 'rate', entryFee: cashGameInfo?.entryFee, currency: cashGameInfo?.currency,
      buyInUSD: cashGameInfo?.buyInUSD, maxPlayers: gameState?.maxPlayers || cashGameInfo?.maxPlayers || 2,
    });
    setTimeout(() => { socket.off('cash-room-joined', onJoined); socket.off('cash-error', onErr); }, 15000);
  }), [socket, actualGameId, roomConfig, cashGameInfo, currentUser, navigate, gameState?.maxPlayers]);

  // Keep the table open while a split offer is waiting for an answer (a player may have closed it).
  const splitOfferStatus = gameState?.splitOffer?.status;
  useEffect(() => {
    if (splitOfferStatus === 'open' && gameState?.pointTable?.length > 0) {
      setPointTableData(gameState.pointTable);
      setShowPointTable(true);
    }
  }, [splitOfferStatus]);

  const handleClosePointTable = useCallback(() => {
    setShowPointTable(false);
    if (isGameOver) {
      setGameEnded(true);
    }
    // If game is NOT over, the server will auto-start the next deal
    // via the 10-second timeout in finalizeSubmissionPhase.
    // The next game-state-update with status 'playing' will trigger
    // the auto-close logic below.
  }, [isGameOver]);

  // 🛡️ DEFENSIVE: Watch for deal_completed / finished status changes in gameState
  // and auto-show the point table. This catches cases where the socket event handlers
  // miss the update (e.g. dropped player reconnection, race conditions, etc.).
  useEffect(() => {
    const status = gameState?.status;
    if ((status === 'deal_completed' || status === 'finished') &&
        gameState?.pointTable && gameState.pointTable.length > 0) {
      console.log(`🛡️ Defensive effect: detected ${status} with pointTable — showing point table`);
      setPointTableData(gameState.pointTable);
      // ?? POINTS RUMMY: Don't show deal numbers — use "Round" instead
      const isPointsRummy = gameState?.gameMode === 'points';
      setPointTableTitle(
        status === 'finished'
          ? '📊 Game Over - Point Table'
          : (isPointsRummy
              ? '📊 Round Complete'
              : `📊 Deal ${gameState.currentDeal || '?'}/${gameState.totalDeals || '?'} Complete`)
      );
      setPointTableMessage(
        gameState.declarationMessage ||
        (status === 'finished'
          ? 'Game over!'
          : (isPointsRummy
              ? 'Round complete! Next round starting soon...'
              : 'Deal completed! Next deal starting soon...'))
      );
      setIsGameOver(status === 'finished');
      setShowPointTable(true);
    }
  }, [gameState?.status, gameState?.pointTable]);

  // 🗑️ REMOVED: handleNextDeal is no longer needed.
  // The server auto-starts the next deal after 10 seconds.
  // Players just close the point table and wait for the new deal.

  useEffect(() => {
    if (!socketReady || !socket) return;
    const handleCardMove = (data) => {
      const { playerId, source, target, card } = data;
      let startRef = null, endRef = null;
      if (source === 'deck') startRef = deckRef;
      else if (source === 'discard') startRef = discardRef;
      else if (source === 'hand') startRef = circleRefs.current[playerId];
      if (target === 'hand') endRef = circleRefs.current[playerId];
      else if (target === 'discard') endRef = discardRef;
      if (!startRef?.current || !endRef?.current) return;
      const startRect = startRef.current.getBoundingClientRect();
      const endRect = endRef.current.getBoundingClientRect();
      setAnimations(prev => [...prev, {
        id: Date.now() + Math.random(),
        card,
        startX: startRect.left + startRect.width / 2,
        startY: startRect.top + startRect.height / 2,
        endX: endRect.left + endRect.width / 2,
        endY: endRect.top + endRect.height / 2,
        startTime: performance.now(),
        duration: 1000,
      }]);
    };
    socket.on('card-move', handleCardMove);
    return () => { socket.off('card-move', handleCardMove); };
  }, [socketReady, socket]);

  useAnimationFrame(() => {
    setAnimations(prev => prev.filter(anim => performance.now() - anim.startTime < anim.duration));
  });

  const renderAnimations = () => {
    if (animations.length === 0) return null;
    return createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2000 }}>
        {animations.map(anim => {
          const progress = Math.min(1, (performance.now() - anim.startTime) / anim.duration);
          const x = anim.startX + (anim.endX - anim.startX) * progress;
          const y = anim.startY + (anim.endY - anim.startY) * progress;
          const src = anim.card.back ? '/images/cards/back.png' : CardUtils.getCardImage(anim.card) || '/images/cards/back.png';
          return (
            <div key={anim.id} style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%, -50%)', width: '70px', height: '98px' }}>
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.5)' }} />
            </div>
          );
        })}
      </div>,
      document.body
    );
  };

  const renderGameTable = () => {
    const disableClicks = actionInProgressRef.current || hasDrawnThisTurn;

    return (
      <div className={`rummy-table-container ${isMobile ? 'mobile' : 'desktop'} landscape`}
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#0a0a1a', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y pinch-zoom' }}>
        {/* Header removed — leave, info & controls moved to a fixed (detached) HUD overlay */}
        <div className="game-main-area" style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden', width: '100%', height: '100%', padding: 0, margin: 0 }}>
          {errorMessage && (
            <div className="error-popup" style={{ position: 'absolute', top: '5px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(220, 53, 69, 0.9)', color: 'white', padding: '8px 16px', borderRadius: '4px', zIndex: 1001, fontSize: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>{errorMessage}</div>
          )}
          {/* 🛑 WRONG DECLARATION POPUP — shows for 3 seconds */}
          {wrongDeclarationInfo?.visible && (
            <div className="wrong-declaration-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10000,
              animation: 'fadeIn 0.3s ease-out'
            }}>
              <div className="wrong-declaration-modal" style={{
                background: 'linear-gradient(135deg, #2a0a0a 0%, #4a1010 100%)',
                border: '3px solid #f44336', borderRadius: '16px',
                padding: '30px 40px', maxWidth: '480px', width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 30px rgba(244,67,54,0.3)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
                <h2 style={{ color: '#f44336', margin: '0 0 8px 0', fontSize: isMobile ? '20px' : '24px' }}>
                  Invalid Declaration!
                </h2>
                <p style={{ color: '#ffcdd2', fontSize: isMobile ? '15px' : '17px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                  <strong style={{ color: '#FFD700' }}>{wrongDeclarationInfo.playerName}</strong>
                  {' '}has made an invalid declaration.
                </p>
                <div style={{
                  background: 'rgba(244,67,54,0.15)',
                  border: '1px solid rgba(244,67,54,0.4)',
                  borderRadius: '8px', padding: '10px 16px',
                  margin: '0 0 16px 0'
                }}>
                  <p style={{ color: '#ef9a9a', fontSize: isMobile ? '13px' : '14px', margin: 0 }}>
                    {wrongDeclarationInfo.message}
                  </p>
                </div>
                {wrongDeclarationInfo.dropped && (
                  <div style={{
                    background: 'rgba(255,152,0,0.15)',
                    border: '1px solid rgba(255,152,0,0.4)',
                    borderRadius: '8px', padding: '8px 12px',
                    marginBottom: '8px'
                  }}>
                    <p style={{ color: '#FFC107', fontSize: isMobile ? '12px' : '13px', margin: 0 }}>
                      ⚠️ Dropped from game with 80 penalty points
                    </p>
                  </div>
                )}
                <p style={{ color: '#aaa', fontSize: isMobile ? '11px' : '12px', margin: 0 }}>
                  Other players will continue the game...
                </p>
              </div>
            </div>
          )}
          {showDeclareConfirm && (
            <div className="declare-confirm-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 9999
            }}>
              <div className="declare-confirm-modal" style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                border: '2px solid #4CAF50', borderRadius: '16px',
                padding: '30px 40px', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                textAlign: 'center'
              }}>
                <h2 style={{ color: '#FFD700', margin: '0 0 12px 0', fontSize: isMobile ? '18px' : '22px' }}>
                  🏆 Declare Rummy
                </h2>
                <p style={{ color: '#e0e0e0', fontSize: isMobile ? '14px' : '16px', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Are you sure you want to declare?
                  <br />
                  <span style={{ color: '#FFC107', fontSize: isMobile ? '12px' : '14px' }}>
                    Your cards will be validated and all players will need to submit their arrangement within 1 minute.
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button onClick={handleDeclareConfirm} style={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                    color: 'white', border: 'none', padding: '10px 28px',
                    borderRadius: '8px', cursor: 'pointer', fontSize: '16px',
                    fontWeight: 'bold', transition: 'transform 0.2s'
                  }}>
                    Yes, Declare
                  </button>
                  <button onClick={handleDeclareCancel} style={{
                    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                    color: 'white', border: 'none', padding: '10px 28px',
                    borderRadius: '8px', cursor: 'pointer', fontSize: '16px',
                    fontWeight: 'bold', transition: 'transform 0.2s'
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {declarationNotification && (
            <div className="declaration-notification" style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              background: 'rgba(76, 175, 80, 0.25)',
              backdropFilter: 'blur(2px)',
              color: '#ffffff', padding: '16px 32px', borderRadius: '12px', zIndex: 1002,
              fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold',
              textShadow: '0 2px 8px rgba(0,0,0,0.8)',
              textAlign: 'center',
              border: '1px solid rgba(76, 175, 80, 0.4)',
              animation: 'pulse-notification 1.5s ease-in-out infinite',
              maxWidth: '80%',
              pointerEvents: 'none',
            }}>
              {declarationNotification}
            </div>
          )}
          <div className="table-container" style={{ position: 'relative', flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0, margin: 0, overflow: 'hidden', background: '#0a0a1a', width: '100%', height: '100%' }}>
            <PinchZoom enabled={isMobile}>
            <GameTable
              gameState={gameState}
              jokerCard={gameState?.wildJoker}
              onDrawFromDeck={handleMultiplayerDrawFromDeck}
              onDrawFromDiscard={handleMultiplayerDrawFromDiscard}
              onShowDiscardModal={() => setShowDiscardModal(true)}
              currentUserId={currentUser?.id}
              isMultiplayer={true}
              isPlayerTurn={contextIsMyTurn}
              isMobile={isMobile}
              containerWidth="100%"
              selectedCardsForGroup={selectedCardsForGroup}
              onCardSelect={handleCardSelect}
              onAutoArrange={handleAutoArrange}
              onCreateGroup={handleCreateGroup}
              onResetGroupSelection={handleResetGroupSelection}
              onDeclaration={handleDeclaration}
              onDiscard={handleDiscard}
              onMoveCard={handleMoveCard}
              onDrop={handleDrop}
              hasDrawnThisTurn={hasDrawnThisTurn}
              deckRef={deckRef}
              discardRef={discardRef}
              circleRefs={circleRefs}
              disabled={disableClicks}
              isSubmissionPhase={isSubmissionPhase}
              hasSubmitted={hasSubmitted}
              submissionTimeLeft={submissionTimeLeft}
              onSubmitArrangement={handleSubmitArrangement}
              isWaitingAsDeclarer={isSubmissionPhase && hasSubmitted}
              declarationNotification={declarationNotification}
              voiceEnabled={voiceEnabled}
              voiceStates={voice.remoteStates}
              seatingPhase={seatingPhase}
              seatingData={seatingData}
              countdown={seatingCountdown}
              firstPlayerId={seatingData?.firstPlayerId || seatingData?.firstPlayer || gameState?.seatingPhase?.firstPlayer}
              dealerPlayerId={gameState?.dealerPlayerId || seatingData?.dealerPlayerId || gameState?.seatingPhase?.dealerPlayerId}
              isWaiting={isWaiting}
          isRoomCreator={isRoomCreator}
          onStartGame={handleStartCustomRoom}
              onDropBlocked={() => { const b = getDropBlock(); if (b) setDropNotice(b); }}
              waitingRoomData={waitingRoomData}
              roomCountdown={roomCountdown}
              isAutoDropQueued={isAutoDropQueued}
              onToggleAutoDrop={handleToggleAutoDrop}
              cashGameInfo={cashGameInfo || (roomConfig?.isCashGame ? roomConfig : null)}
              roomConfig={roomConfig}
            />
            </PinchZoom>
          </div>
          {/* 🛑 CONFIRMATION MODAL: DROP */}
          {showDropConfirm && (
            <div className="custom-confirm-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10005
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                border: '2px solid #f44336', borderRadius: '16px',
                padding: '28px 32px', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)', textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🃏</div>
                <h3 style={{ color: '#ff5252', margin: '0 0 10px 0', fontSize: isMobile ? '18px' : '22px' }}>Drop from Game?</h3>
                <p style={{ color: '#e0e0e0', fontSize: isMobile ? '13px' : '15px', lineHeight: '1.5', margin: '0 0 12px 0' }}>
                  You will receive a <strong>{hasDrawnThisTurn || currentPlayer?.hasDrawnAnyCard ? '40 point (Middle Drop)' : `${Number(gameState?.maxPoints) === 101 ? 20 : 25} point (First Drop)`}</strong> penalty.
                </p>
                <p style={{ color: '#aaa', fontSize: '12px', margin: '0 0 20px 0' }}>Are you sure you want to drop out of this round?</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button onClick={() => setShowDropConfirm(false)} style={{
                    background: 'transparent', border: '1px solid #777', color: '#ccc',
                    padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                  }}>Cancel</button>
                  <button onClick={handleConfirmDrop} style={{
                    background: 'linear-gradient(135deg, #d32f2f, #b71c1c)', color: '#fff',
                    border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                  }}>Yes, Drop</button>
                </div>
              </div>
            </div>
          )}
          {/* 🛑 CONFIRMATION MODAL: AUTO DROP */}
          {dropNotice && (
            <div className="custom-confirm-overlay" onClick={() => setDropNotice(null)} style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10006
            }}>
              <div onClick={(e) => e.stopPropagation()} style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                border: '2px solid #f44336', borderRadius: '16px',
                padding: '26px 30px', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)', textAlign: 'center'
              }}>
                <div style={{ fontSize: '38px', marginBottom: '8px' }}>🚫</div>
                <h3 style={{ color: '#ff8a80', margin: '0 0 10px 0', fontSize: isMobile ? '18px' : '22px' }}>You cannot drop</h3>
                <p style={{ color: '#e0e0e0', fontSize: isMobile ? '13px' : '15px', lineHeight: '1.5', margin: '0 0 10px 0' }}>
                  You have <strong>{dropNotice.cumulative}</strong> points. A {dropNotice.penalty === 40 ? 'middle' : 'first'} drop adds <strong>{dropNotice.penalty}</strong>, which takes you to <strong>{dropNotice.cumulative + dropNotice.penalty}</strong> — the {dropNotice.maxPoints}-point limit.
                </p>
                <p style={{ color: '#ffcc80', fontSize: '13px', margin: '0 0 18px 0' }}>
                  If you drop you will be <strong>eliminated</strong>. You must keep playing this round.
                </p>
                <button onClick={() => setDropNotice(null)} style={{
                  background: 'linear-gradient(135deg, #f57c00, #e65100)', color: '#fff',
                  border: 'none', padding: '10px 28px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                }}>OK, I'll keep playing</button>
              </div>
            </div>
          )}
          {showAutoDropConfirm && (
            <div className="custom-confirm-overlay" style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10005
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                border: '2px solid #ff9800', borderRadius: '16px',
                padding: '28px 32px', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)', textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🛑</div>
                <h3 style={{ color: '#ffb74d', margin: '0 0 10px 0', fontSize: isMobile ? '18px' : '22px' }}>Queue Auto Drop?</h3>
                <p style={{ color: '#e0e0e0', fontSize: isMobile ? '13px' : '15px', lineHeight: '1.5', margin: '0 0 12px 0' }}>
                  You will be automatically dropped (<strong>{Number(gameState?.maxPoints) === 101 ? 20 : 25} pts</strong> first drop) every time your turn arrives, <strong>every round</strong>, until you switch Auto Drop off.
                </p>
                <p style={{ color: '#ffcc80', fontSize: '12px', margin: '0 0 20px 0' }}>
                  In a 2-player game, <strong>3 auto drops count as leaving</strong> and your opponent wins. With more players it continues until your next drop would reach the points limit — then you must play.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button onClick={() => setShowAutoDropConfirm(false)} style={{
                    background: 'transparent', border: '1px solid #777', color: '#ccc',
                    padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                  }}>Cancel</button>
                  <button onClick={handleConfirmAutoDrop} style={{
                    background: 'linear-gradient(135deg, #f57c00, #e65100)', color: '#fff',
                    border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                  }}>Yes, Auto Drop</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (gameEnded && !showPointTable && !isSubmissionPhase) {
    return (
      <div className="game-ended-screen">
        <h2>Game Over</h2>
        <button onClick={handleRedirectToLobby}>🏠 Return to Main Lobby</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enhanced-rummy-game error">
        <h2>Game Error</h2><p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
        <button onClick={onLeaveGame || handleRedirectToLobby}>Exit</button>
      </div>
    );
  }

  const currentPlayer = getCurrentPlayer();
  const hasCards = currentPlayer?.cards?.length > 0;
  const isDropped = currentPlayer?.dropped === true;
  // 🎯 POINTS RUMMY FIX: Allow eliminated/dropped players to spectate the game
  // while other players continue playing. Without this, they'd see a loading
  // spinner because their hand is empty (hasCards=false) even though the game
  // is still active.
  const isSpectating = isDropped && (gameState?.status === 'playing' || gameState?.status === 'started' || gameState?.status === 'submission');
  const isGamePlaying = (gameState?.status === 'playing' || gameState?.status === 'started')
    ? (hasCards || isSpectating)
    : gameState?.status === 'submission';
  // 🛠️ FIX: Also accept 'deal_completed' as a valid status for rendering
  // (when a drop in deal rummy ends just the current deal, not the whole game).
  // Show point table when status is 'finished' or 'deal_completed' with showPointTable=true.
  const isFinishedWithPointTable = (gameState?.status === 'finished' || gameState?.status === 'deal_completed') && showPointTable;

  // 🎯 CRITICAL FIX: A dropped player should NEVER see the loading spinner.
  // They need to see either the point table (deal_completed/finished) or spectate
  // the ongoing game. Without this, the guard fails because isDropped=true, hasCards=false,
  // and if showPointTable hasn't been set yet by the socket handler, the guard evaluates
  // to true and shows the spinner indefinitely.
  const isDroppedPlayer = gameState?.players?.find(
    p => String(p.userId) === String(currentUser?.id)
  )?.dropped === true;
  
  // Waiting stage = before seating/play. Once seating starts (or the engine reports any later status) it is over for good.
  const gameStatusNow = gameState?.status;
  const gameHasStarted = ['seating', 'playing', 'started', 'submission', 'deal_completed', 'finished', 'cancelled'].includes(gameStatusNow);
  const isWaiting = !isPractice && !seatingPhase && !leftWaiting && !gameHasStarted &&
    (gameStatusNow === 'waiting' || (!gameState && !!waitingRoomData));

  // Table is "in play" only once cards are dealt: everything except mic / speaker / music stays hidden before that.
  const gameStarted = !seatingPhase && !isWaiting &&
    ['playing', 'started', 'submission', 'deal_completed', 'finished'].includes(gameStatusNow);

  const isRoomCreator = !!roomConfig?.isCustomRoom && (
    roomConfig?.isCreator === true ||
    (waitingRoomData?.creatorId != null && String(waitingRoomData.creatorId) === String(currentUser?.id))
  );

  // Custom room: the host starts the game manually (same socket event the old lobby used)
  const handleStartCustomRoom = () => new Promise((resolve) => {
    const code = roomConfig?.roomCode || waitingRoomData?.roomCode;
    if (!socket || !code) { resolve({ success: false, error: 'Not connected to game server' }); return; }
    const timer = setTimeout(() => resolve({ success: false, error: 'Start request timed out' }), 15000);
    socket.emit('start-custom-room', { code }, (res) => {
      clearTimeout(timer);
      resolve(res || { success: false, error: 'No response from server' });
    });
  });
  const shouldShowTable = socketReady && (
    isGamePlaying || seatingPhase || showPointTable || isFinishedWithPointTable || isDroppedPlayer || isWaiting
  );
  
  if (!shouldShowTable) {
    return <div className="enhanced-rummy-game loading"><div className="loading-spinner" /></div>;
  }

  // ==================== DETACHED HUD (fixed overlay, unaffected by table zoom/pan) ====================
  const renderGameHUD = () => {
    const roomCode = gameState?.roomCode || actualGameId;
    const playerCount = gameState?.playerCount || gameState?.players?.length || 0;
    const maxPlayers = gameState?.maxPlayers || playerCount || 0;
    const isDeal = gameState?.gameMode === 'deal';
    const dealLabel = isDeal
      ? `Deal ${gameState?.currentDeal || 1}/${gameState?.totalDeals || 1}`
      : (gameState?.gameMode === 'rate'
          ? `Points ${gameState?.ratePerPointUSD || ''} USD/pt`
          : `Pool ${gameState?.maxPoints || 201}`);
    const win = gameState?.gameMode === 'rate' ? null : cashGameInfo?.winningAmount;
    const currency = cashGameInfo?.currency;

    return createPortal(
      <div className={`game-hud ${isMobile ? 'mobile' : 'desktop'} ${isLandscape ? 'landscape' : ''}`}>
        {/* Top-left: leave + info */}
        <div className="hud-top-left">
          <button data-tour="leave" className="hud-leave-btn" onClick={onLeaveGame || handleRedirectToLobby} title="Leave game">← Leave</button>
          {isPractice && (
            <div className="hud-settings" style={{ position: 'relative', display: 'inline-block' }}>
              <button
                data-tour="settings"
                className="hud-settings-btn"
                onClick={() => setShowSettingsMenu(v => !v)}
                title="Settings"
                style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', border: '1px solid #444', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '14px' }}
              >
                ⚙️
              </button>
              {showSettingsMenu && (
                <div className="hud-settings-menu" style={{ position: 'absolute', top: '110%', left: 0, background: '#1b1b32', border: '1px solid #33334d', borderRadius: '8px', padding: '6px', minWidth: '160px', zIndex: 5000, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  <button
                    onClick={() => { setShowSettingsMenu(false); window.dispatchEvent(new CustomEvent('replay-tutorial')); }}
                    style={{ background: 'none', border: 'none', color: '#eaeaf5', cursor: 'pointer', fontSize: '13px', padding: '8px 10px', width: '100%', textAlign: 'left', borderRadius: '6px' }}
                  >
                    Replay Tutorial
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="hud-info-chips">
            <span className="hud-chip room">Room {roomCode?.substring(0, 6)}</span>
            <span className="hud-chip players">👥 {playerCount}/{maxPlayers}</span>
            {win != null && (
              <span className="hud-chip win">🏆 Win {CurrencyService?.format ? CurrencyService.format(win, currency) : `${win} ${currency}`}</span>
            )}
            <span className="hud-chip mode">{isDeal ? '🃏' : '🎯'} {dealLabel}</span>
            {gameState?.wildJoker && <span className="hud-chip joker">J {gameState.wildJoker.rank}</span>}
            <span className={`hud-chip turn ${contextIsMyTurn ? 'my-turn' : ''}`}>{contextIsMyTurn ? '🎯 Your Turn' : '⏳ Waiting'}</span>
          </div>
        </div>
        {/* Right-side: action buttons */}
        {gameStarted && !isSubmissionPhase && (
          <div className="hud-right-actions">
            <button className="hud-action arrange" onClick={handleAutoArrange} title="Auto arrange cards">Arrange</button>
            {selectedCardsForGroup?.length >= 2 && (
              <button className="hud-action group" onClick={handleCreateGroup} title="Group selected cards">Group ({selectedCardsForGroup?.length})</button>
            )}
            {selectedCardsForGroup?.length > 0 && (
              <button className="hud-action clear" onClick={handleResetGroupSelection} title="Clear selection">Clear</button>
            )}
            {/* Declare / Discard now appear beside the player's hand (GameTable) once a card is selected */}
            {/* Middle drop rule: Drop is inactive once a card has been drawn this turn (deck OR discard) */}
            <button
              className="hud-action drop"
              onClick={() => { const b = getDropBlock(); if (b) setDropNotice(b); else setShowDropConfirm(true); }}
              disabled={!contextIsMyTurn || hasDrawnThisTurn}
              title={hasDrawnThisTurn ? 'Drop is available again from your next turn' : 'Drop from game'}
              style={{ opacity: (contextIsMyTurn && !hasDrawnThisTurn) ? 1 : 0.4, cursor: (contextIsMyTurn && !hasDrawnThisTurn) ? 'pointer' : 'not-allowed' }}
            >
              Drop
            </button>
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderAnimations()}
      {renderGameTable()}
      {renderGameHUD()}
      {/* Transparent left-side bar: mic, speaker, game sound, previous rounds (points) */}
      {!seatingPhase && createPortal(
        <GameSideBar
          gameStarted={gameStarted}
          onShowDecks={() => setShowDecks(true)}
          onShowRounds={() => setShowRounds(v => !v)}
          voiceEnabled={voiceEnabled}
          micOn={voice.micOn}
          speakerOn={voice.speakerOn}
          permissionDenied={voice.permissionDenied}
          onToggleMic={voice.toggleMic}
          onToggleSpeaker={voice.toggleSpeaker}
          onOpenMicSettings={voice.openAppSettings}
          soundOn={soundOn}
          onToggleSound={handleToggleSound}
          onShowPoints={() => setShowDealHistory(true)}
          isMobile={isMobile}
          canAutoDrop={!isPractice && (
            // while the mode is ON the button stays visible (even when dropped / between rounds) so it can be switched off
            isAutoDropQueued ||
            (!isDropped && gameState?.status === 'playing' &&
              !gameState?.players?.find(p => String(p.userId) === String(currentUser?.id))?.hasDrawnAnyCard)
          )}
          isAutoDropQueued={isAutoDropQueued}
          onToggleAutoDrop={handleToggleAutoDrop}
        />,
        document.body
      )}
      {/* Round-by-round points table (left pane). The "last declared hand" point table stays as is. */}
      {!isPractice && gameStarted && (
        <RoundPointsPanel
          open={showRounds}
          onClose={() => setShowRounds(false)}
          socket={socket}
          gameId={actualGameId}
          gameState={gameState}
          isMobile={isMobile}
          currentUserId={currentUser?.id}
        />
      )}
      <CardDeckPicker show={showDecks} onClose={() => setShowDecks(false)} current={cardDeck} onChoose={chooseDeck} isMobile={isMobile} />
      <DealHistoryModal
        show={showDealHistory}
        onClose={() => setShowDealHistory(false)}
        socket={socket}
        gameId={actualGameId}
        gameState={gameState}
      />
      {/* Portrait fallback (iOS / browsers that refuse the orientation lock): ask to rotate */}
      {isMobile && needsRotate && !seatingPhase && createPortal(
        <div
          onClick={tryLockLandscape}
          style={{
            position: 'fixed', inset: 0, zIndex: 200000, background: 'rgba(5,10,25,0.96)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: '#fff', textAlign: 'center', padding: '24px', fontFamily: 'Arial, sans-serif'
          }}
        >
          <div style={{ fontSize: '56px', marginBottom: '12px' }}>📱↻</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '22px' }}>Rotate your phone</h2>
          <p style={{ margin: '0 0 18px 0', fontSize: '14px', color: '#ccc' }}>The game table is played in landscape mode.</p>
          <button
            onClick={(e) => { e.stopPropagation(); tryLockLandscape(); }}
            style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 22px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
          >
            Tap to switch to landscape
          </button>
        </div>,
        document.body
      )}
      {/* Portal to body so the point table modal sits above the fixed HUD controls */}
      {createPortal(
        <PointTableModal
          show={showPointTable}
          onClose={handleClosePointTable}
          pointTable={pointTableData}
          title={pointTableTitle}
          message={pointTableMessage}
          gameOver={isGameOver}
          countdown={pointTableCountdown}
          splitOffer={gameState?.splitOffer || null}
          currentUserId={currentUser?.id}
          onSplitResponse={handleSplitResponse}
          rejoin={gameState?.rejoin || null}
          onRejoin={handleRejoin}
          rateResult={gameState?.gameMode === 'rate' ? (gameState?.rateResult || null) : null}
          ratePerPointUSD={gameState?.gameMode === 'rate' ? gameState?.ratePerPointUSD : null}
          onPlayAgain={gameState?.gameMode === 'rate' && isGameOver ? handlePlayAgain : null}
        />,
        document.body
      )}
    </>
  );
};

export default EnhancedRummyTable;